import { RootState } from 'app/store';
import { EControlType } from 'components/ControlWrapper';
import { AppLinkToExternal, AppNavigateToExternal, buildBenefitStatusOptions, buildDeactivateOptions, buildGroupConfigurationStatusOptions, buildProductOptions, calcFieldName, compareUnderWritingReviewToWithoutDispatch, ConvertToDropdownOptions, createAttributeSet, createOpportunityOptionAndBenefits, emitCustomEvent, EViewType, friendlyValue, URIVersionToBlobVersion } from 'global/Application';
import { EmitEventProcess } from 'global/EmitEvent/EmitEventManager';
import {
    ICompareGroupConfigurationData,
    loadLeftSnap,
    loadMergeSnap,
    loadRightSnap, transformLeftSnapForCompare,
    updatePharmacyStatus
} from 'global/Reducer/compSlice';
import { addUpdateGroupConfiguration, IGCData, purgeOnData, setSaveTokenOnData, updateFeesAndPremiums, updateIteration, updateModifiedOn, updateSnapshotData } from 'global/Reducer/dataSlice';
import { CloneTransformer } from 'global/Transform/CloneTransformer';
import { EGroupCollectIntegrationStatus, GroupCollectEmitEventTransformer } from 'global/Transform/GroupCollectEmitEventTransformer';
import { EJsonTransformerRuntime, JsonTransformer } from 'global/Transform/JsonTransformer';
import { PharmacyResetTransformer } from 'global/Transform/PharmacyResetTransformer';
import { RemoveDeactivatedDataTransformer } from 'global/Transform/RemoveDeactivatedDataTransformer';
import { RevisionResetTransformer } from 'global/Transform/RevisionResetTransformer';
import { IFieldVisibility } from 'global/Visibility/IFieldVisibility.type';
import { ParentVisibilityGraphProcessor } from 'global/Visibility/ParentVisibilityGraph';
import * as IXrmOptions from 'model/AttributeOptionSets.type';

import { IJsonTransformer } from 'global/Transform/IJsonTransformer';
import { UpgradeReleaseTransformer } from 'global/Transform/UpgradeReleaseTransformer';
import * as IXrmTypes from 'model/CRM';
import * as IXrmTypesExtended from 'model/CRM.type';
import { buildAttributeSetFormDefinitions, buildCategoryFormDefinitions, buildNonStandardFormDefinitions } from 'model/FlexibleFormModel';
import * as IXrmGlobalOptions from 'model/GlobalOptionSetStatic';
import { ICheckList } from 'model/ICheckList';
import { IGroupConfigurationUpdate } from 'model/IGroupConfigurationUpdate';
import { blankSnapshot } from 'model/snapshotTemplate';
import { Dispatch } from 'redux';
import * as FAM from '../../model/FlexibleAttributeModel';
import {
    bcbs_statecode_byStatus,
    IOptionSetValue,
    IPharmacyORE
} from "../../model/FlexibleAttributeModel";
import { AppDataSource, ISaveResult, ISnapshotInfo } from '../DataSources/AppDataSource';
import { loadSnapshotsData } from "../Reducer/compSlice";
import {
    changeSnapshotStatus, defaultStatusSlice, ELoadStatus,
    endSnapshotLoad,
    ESnapshotStatus, IGroupConfigurationStatus,
    purgeOnStatus,
    setConcurrencyConfirmationModal,
    setProcessing,
    setStatusRecycle,
    setViewType,
    startSnapshotLoad,
    updateAccount, updateCreatingStatus, updateDataSavingStatus,
    updateDebug,
    updateLastSaved, updateSnapshotBlobVersion, updateSnapshots
} from '../Reducer/statusSlice';
import { ArrayToLookup, copyPropertiesOnly, Delay, formatDate, hasValue, objectFlip, parseDate, timestamp, trimGUIDLower } from '../Utility/TSFunctions';
import { appInsights } from './AppInsight';
import { Constants } from './Constants';
import { ValidationError } from './ValidationError';
import { AutoCalculateTransformer } from 'global/Transform/AutoCalculateTransformer';

interface ILoadSnapshotInstructions {
    leftSnapshot?: IXrmTypes.bcbs_groupconfigurationsnapshot;
    leftSnapshotId?: FAM.TGuid;

    rightSnapshot?: IXrmTypes.bcbs_groupconfigurationsnapshot;
    rightSnapshotId?: FAM.TGuid;

    compareTransformReady?: boolean;

    primarySnapshot?: IXrmTypes.bcbs_groupconfigurationsnapshot;
    primarySnapshotId?: FAM.TGuid;
    primarySnapshotVersionId?: string;

    purgeStore?: Boolean;

    // TODO until we parallel load
    context?: "primary" | "left" | "right" | "?";

    merge?: boolean;
    liveLoad?: boolean;
}

export interface ICloneResult {
    snapshot: IXrmTypes.bcbs_groupconfigurationsnapshot;
    change: Partial<IXrmTypesExtended.IUpdatable_bcbs_groupconfigurationsnapshot>;
    release: IXrmTypes.bcbs_config_release;
}

export enum ESaveType {
    EDefault, ECreate, EManual, EAuto, ESubmit, EUpgradeRelease, EConcurrencyConflict,
}

export interface ISaveInstruction {
    saveType?: ESaveType
    ignoreJsonSave?: boolean;
    isMergeSave?: boolean;
    isUnderwritingSave?: boolean;

    onPostSaveNavigateExternalToSaveResult?: boolean;
    onPreSaveUpdateJson?: IGroupConfigurationUpdate

    onSaveClone?: boolean;
    onSaveSetStatusCode?: number;
    onSaveGenerateGoldStandardAndSQL?: boolean;
    onSaveChangeUWConfirmedParentId?: string;
    onSaveBypassUnderwritingTaskGeneration?: boolean;
    onSaveSetSnapshotName?: string;
    onSaveStatusScreen?: boolean;
    onSaveCreateEnrollmentDetail?: IXrmTypes.bcbs_enrollmentdetails
    onSaveUpdateRelease?: boolean;
    onSaveForceStatusCodeToInProgress?: boolean;

    onPostSaveIgnoreReload?: boolean;
    onPostSaveSetViewOnly?: boolean;

    onCloneSetOriginatingSnapshotId?: string;
}

const CloneSnapshotAsync = async (
    dataSource: AppDataSource,
    xSnapshot: IXrmTypes.bcbs_groupconfigurationsnapshot,
    state: RootState,
    instructions?: ISaveInstruction
) => {

    appInsights?.trackEvent({
        name: 'CloneSnapshotAsync',
        properties: {
            bcbs_groupconfigurationsnapshotid: xSnapshot.bcbs_groupconfigurationsnapshotid,
            instructions: instructions
        }
    });

    let nSnapshot: Partial<IXrmTypesExtended.IUpdatable_bcbs_groupconfigurationsnapshot> = {}

    // bcbs_groupconfigurationsnapshot[required]:
    // ----------------------------------------------------------------------
    nSnapshot.bcbs_name = xSnapshot.bcbs_name;
    nSnapshot.statuscode = xSnapshot.statuscode;
    nSnapshot.statecode = xSnapshot.statecode;

    // bcbs_name
    // ----------------------------------------------------------------------
    // newSnapshot.bcbs_name = xSnapshot.bcbs_name;

    // bcbs_groupid (account)
    // ----------------------------------------------------------------------
    if (xSnapshot._bcbs_groupid_value)
        nSnapshot["bcbs_groupid@odata.bind"] = `/accounts(${xSnapshot._bcbs_groupid_value})`;

    // bcbs_Opportunityid (opportunity)
    // ----------------------------------------------------------------------
    // The Opportunity field should be NULL when a GC is created from another GC.
    // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/283003
    // if (snapshot._bcbs_opportunityid_value)
    //     newSnapshot["bcbs_Opportunityid@odata.bind"] = `/opportunities(${snapshot._bcbs_opportunityid_value})`;

    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot._bcbs_opportunityid_value)
            nSnapshot["bcbs_Opportunityid@odata.bind"] = `/opportunities(${xSnapshot._bcbs_opportunityid_value})`;
    }

    // bcbs_OriginatingSnapshotid
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot._bcbs_originatingsnapshotid_value)
            nSnapshot["bcbs_OriginatingSnapshotid@odata.bind"] = `/bcbs_groupconfigurationsnapshots(${xSnapshot._bcbs_originatingsnapshotid_value})`;
    }

    if (xSnapshot._bcbs_originatingsnapshotid_value)
        nSnapshot["bcbs_OriginatingSnapshotid@odata.bind"] = `/bcbs_groupconfigurationsnapshots(${xSnapshot._bcbs_originatingsnapshotid_value})`;

    if (instructions?.onCloneSetOriginatingSnapshotId)
        nSnapshot["bcbs_OriginatingSnapshotid@odata.bind"] = `/bcbs_groupconfigurationsnapshots(${instructions?.onCloneSetOriginatingSnapshotId})`;

    // bcbs_OriginatingSnapshot2id
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot._bcbs_originatingsnapshot2id_value)
            nSnapshot["bcbs_OriginatingSnapshot2id@odata.bind"] = `/bcbs_groupconfigurationsnapshots(${xSnapshot._bcbs_originatingsnapshot2id_value})`;
    }
    // Bug https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/278707
    // The only time Originating Snapshot 2 value is set is when the record is the result of the Merge process, not the Create process.
    if (instructions?.isMergeSave && xSnapshot._bcbs_originatingsnapshot2id_value)
        nSnapshot["bcbs_OriginatingSnapshot2id@odata.bind"] = `/bcbs_groupconfigurationsnapshots(${xSnapshot._bcbs_originatingsnapshot2id_value})`;

    // bcbs_GroupCollectFinancialConfigurationid@odata.bind
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot._bcbs_groupcollectfinancialconfigurationid_value) {
            nSnapshot["bcbs_GroupCollectFinancialConfigurationid@odata.bind"] = `/bcbs_financialconfigurations(${xSnapshot._bcbs_groupcollectfinancialconfigurationid_value})`;
        }
    }
    // confirmed with MUal, Group Collect Integration should not be carried over on any record, merge, create new, save concurrency, ...
    // if (snapshot._bcbs_groupcollectfinancialconfigurationid_value)
    //     newSnapshot["bcbs_GroupCollectFinancialConfigurationid@odata.bind"] = `/bcbs_financialconfigurations(${snapshot._bcbs_groupcollectfinancialconfigurationid_value})`;

    // bcbs_underwritingreviewcompletedate
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot.bcbs_underwritingreviewcompletedate)
            nSnapshot.bcbs_underwritingreviewcompletedate = xSnapshot.bcbs_underwritingreviewcompletedate
    }
    // // had a conversation with MUal, this field should never carry over:
    // // It should be handled by the flow 100% of the time
    // if (snapshot.bcbs_underwritingreviewcompletedate)
    //     data.bcbs_underwritingreviewcompletedate = snapshot.bcbs_underwritingreviewcompletedate;

    // bcbs_ismnil
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot.bcbs_ismnil)
            nSnapshot.bcbs_ismnil = xSnapshot.bcbs_ismnil;
    }

    // bcbs_isreconciliation
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot.bcbs_isreconciliation)
            nSnapshot.bcbs_isreconciliation = xSnapshot.bcbs_isreconciliation;
    }

    // bcbs_isparentoverriddenbyuw
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot.bcbs_isparentoverriddenbyuw)
            nSnapshot.bcbs_isparentoverriddenbyuw = xSnapshot.bcbs_isparentoverriddenbyuw;
    }

    // bcbs_UWConfirmedParentid
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot._bcbs_uwconfirmedparentid_value)
            nSnapshot["bcbs_UWConfirmedParentid@odata.bind"] = `/bcbs_groupconfigurationsnapshots(${xSnapshot._bcbs_uwconfirmedparentid_value})`;
    }

    // bcbs_shortname
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot.bcbs_shortname)
            nSnapshot.bcbs_shortname = xSnapshot.bcbs_shortname;
    }

    // bcbs_effectivedate
    // ----------------------------------------------------------------------
    if (xSnapshot.bcbs_effectivedate)
        nSnapshot.bcbs_effectivedate = xSnapshot.bcbs_effectivedate;

    // bcbs_iseffectivedategoldstandard
    // ----------------------------------------------------------------------
    // // had a conversation with MUal, this field should never carry over:
    // // It should be handled by the flow 100% of the time
    // if (snapshot.bcbs_iseffectivedategoldstandard)
    //     data.bcbs_iseffectivedategoldstandard = snapshot.bcbs_iseffectivedategoldstandard;

    if (instructions?.saveType === ESaveType.EUpgradeRelease)
        if (xSnapshot.bcbs_iseffectivedategoldstandard)
            nSnapshot.bcbs_iseffectivedategoldstandard = xSnapshot.bcbs_iseffectivedategoldstandard;

    // bcbs_fundingtypecode
    // ----------------------------------------------------------------------
    if (xSnapshot.bcbs_fundingtypecode)
        nSnapshot.bcbs_fundingtypecode = xSnapshot.bcbs_fundingtypecode;

    // bcbs_changetypecode
    // ----------------------------------------------------------------------
    if (xSnapshot.bcbs_changetypecode)
        nSnapshot.bcbs_changetypecode = xSnapshot.bcbs_changetypecode;

    // for merge we will not be changing changetype from json
    // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/278711
    if (!instructions?.isMergeSave)
        if (state.data?.json?.changeType?.value)
            nSnapshot.bcbs_changetypecode = state.data.json.changeType.value;

    // bcbs_appurl
    // ----------------------------------------------------------------------
    // // app url should not be copied as is from the old snapshot, in case the secondary save fails
    // // it will cause the snapshot to point to source snapshot
    // if (snapshot.bcbs_appurl)
    //     newSnapshot.bcbs_appurl = snapshot.bcbs_appurl;

    // bcbs_benefitcategorylist
    // ----------------------------------------------------------------------
    if (xSnapshot.bcbs_benefitcategorylist)
        nSnapshot.bcbs_benefitcategorylist = xSnapshot.bcbs_benefitcategorylist;

    // bcbs_Releaseid
    // ----------------------------------------------------------------------
    const published = await dataSource.getPublishedRelease();
    nSnapshot["bcbs_Releaseid@odata.bind"] = `/bcbs_config_releases(${published.bcbs_config_releaseid})`;
    // if (xSnapshot._bcbs_releaseid_value)
    //     nSnapshot["bcbs_Releaseid@odata.bind"] = `/bcbs_config_releases(${xSnapshot._bcbs_releaseid_value})`;
    // // if (instructions?.saveType === ESaveType.EUpgradeRelease) {
    //     nSnapshot["bcbs_Releaseid@odata.bind"] = `/bcbs_config_releases(${published.bcbs_config_releaseid})`;
    // }


    // ownerid
    // ----------------------------------------------------------------------
    if (xSnapshot._owningteam_value)
        nSnapshot["ownerid@odata.bind"] = `/teams(${xSnapshot._owningteam_value})`;

    // bcbs_operativedate
    // ----------------------------------------------------------------------
    nSnapshot.bcbs_operativedate = formatDate(new Date(), 'local:yyyy-MM-ddT00:00:00Z');

    // statuscode/statecode
    // -----------------------------------
    if (instructions?.onSaveSetStatusCode) {
        nSnapshot.statuscode = instructions.onSaveSetStatusCode;
        nSnapshot.statecode = bcbs_statecode_byStatus[instructions.onSaveSetStatusCode];
    }

    // Enrollment Detail
    // -----------------------------------
    const changeEnrollmentDetail: Partial<IXrmTypes.bcbs_enrollmentdetails> & { [key: string]: any } = {
        "bcbs_name": `Group Configuration - ${timestamp()}`,
        "statuscode": 1,
        "statecode": 0,
    }

    // bcbs_enrollmentdetails.ownerid
    // -----------------------------------
    if (xSnapshot._owningteam_value)
        changeEnrollmentDetail["ownerid@odata.bind"] = `/teams(${xSnapshot._owningteam_value})`;

    const enrollmentDetailResult = await dataSource.createAndReturnEnrollmentDetail(changeEnrollmentDetail);

    if (enrollmentDetailResult.bcbs_enrollmentdetailsid)
        nSnapshot['bcbs_EnrollmentDetailsid@odata.bind'] = `/bcbs_enrollmentdetailses(${enrollmentDetailResult.bcbs_enrollmentdetailsid})`;

    // bcbs_releaseupgradedfromid
    // -----------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease)
        nSnapshot['bcbs_ReleaseUpgradedFromid@odata.bind'] = `/bcbs_groupconfigurationsnapshots(${xSnapshot.bcbs_groupconfigurationsnapshotid})`;

    const clonedSnapshot = await dataSource.createGroupConfigurationSnapshot(nSnapshot);

    const cloneResult: ICloneResult = {
        snapshot: clonedSnapshot,
        change: nSnapshot,
        release: published,
    };

    console.log('[appl] dataActions.CloneSnapshotAsync cloneResult', cloneResult);

    return cloneResult;
}

// export const OREChangeDetection = async (
//     dataSourceJson: {
//         snapshots: IXrmTypes.bcbs_groupconfigurationsnapshot[];
//         goldStandards: { bcbs_groupconfigurationsnapshotid: string }[];
//     },
//     state: RootState,
//     dispatch: Dispatch<any>,
//     dataSource: AppDataSource,
// ) => {

//     const liveLoad = false;

//     const dataSource_snapshots_by_bcbs_groupconfigurationsnapshotid = ArrayToLookupByKey(dataSourceJson.snapshots, 'bcbs_groupconfigurationsnapshotid');

//     const config = state.data.config;

//     const _load = async (snapshotId: string) => {

//         if (liveLoad) {
//             const snapshotId_lc = snapshotId.toLowerCase();
//             const snapshot = await dataSource.getGroupConfigurationSnapshot(snapshotId_lc)
//             const accountSnapshots = await dataSource.getGroupConfigurationSnapshotsForAccount(snapshot._bcbs_groupid_value!);

//             return { snapshot, accountSnapshots };
//         }
//         else {
//             const snapshot = dataSource_snapshots_by_bcbs_groupconfigurationsnapshotid[snapshotId];
//             const accountSnapshots = dataSourceJson.snapshots.filter(q => q._bcbs_groupid_value === snapshot._bcbs_groupid_value);

//             return { snapshot, accountSnapshots };
//         }
//     };

//     const _loadJson = async (snapshot: IXrmTypes.bcbs_groupconfigurationsnapshot) => {

//         let json = null as unknown as FAM.IGroupConfigurationData;

//         if (liveLoad) {

//             let result = await dataSource.downloadGroupConfigurationSnapshot(snapshot);
//             json = result?.json!;

//         } else {

//             const f1 = await fetch('http://localhost:7071/api/HttpStartLoadJsonLocal?snapshotId=' + snapshot.bcbs_groupconfigurationsnapshotid);
//             json = await f1.json() as FAM.IGroupConfigurationData;
//         }

//         // const loadTransformer = JsonTransformer.getJsonTransformerForMode(
//         //     EJsonTransformMode.Edit,
//         //     config,
//         //     dataSource,
//         //     snapshot
//         // );

//         // if (loadTransformer)
//         //     await loadTransformer.transformAsync(json, snapshot);

//         return json;

//     };

//     const _compare = (snapshot: IXrmTypes.bcbs_groupconfigurationsnapshot, parentSnapshot: IXrmTypes.bcbs_groupconfigurationsnapshot | undefined | null, snapshotJson: FAM.IGroupConfigurationData, parentSnapshotJson: FAM.IGroupConfigurationData | null) => {

//         let ret = {
//             bcbs_groupconfigurationsnapshotId: snapshot.bcbs_groupconfigurationsnapshotid,
//             parent_bcbs_groupconfigurationsnapshotId: parentSnapshot?.bcbs_groupconfigurationsnapshotid,
//             changed: false,
//             changedPlans: [],
//             error: null as null | string,
//         };

//         if (!parentSnapshotJson) {
//             ret.error = 'no parent snapshot'
//             return ret;
//         };

//         const pharmacyCategory = config.cache?.map_bcbs_config_category_named.Pharmacy;
//         const medicalCategory = config.cache?.map_bcbs_config_category_named.Medical;
//         const pharmBenefits = snapshotJson.benefits.filter(b => b.bcbs_config_categoryid === pharmacyCategory.bcbs_config_categoryid);
//         const medBenefits = snapshotJson.benefits.filter(b => b.bcbs_config_categoryid === medicalCategory.bcbs_config_categoryid);
//         //Filter to only the plans on the snapshot that have a pharmacy benefit
//         const plansWithPharm = snapshotJson.plans.filter(p => p.benefitRefs.some(r => pharmBenefits.find(pb => pb.id === r.id)));
//         plansWithPharm.map(plan => {
//             let change = false;
//             let field1Changed = false;
//             let field2Changed = false;
//             let field3Changed = false;
//             let field4Changed = false;
//             let field5Changed = false;
//             let field6Changed = false;
//             let field7Changed = false;
//             let field8Changed = false;
//             let field9Changed = false;
//             let field10Changed = false;
//             let field11Changed = false;
//             let field12Changed = false;
//             let field13Changed = false;
//             let field14Changed = false;
//             let field15Changed = false;

//             const pharm = pharmBenefits.find(b => plan.benefitRefs.find(br => b.id === br.id));
//             const med = medBenefits.find(b => plan.benefitRefs.find(br => b.id === br.id));
//             const innref = med?.tiers?.find(t => t.bcbs_name === "In Network");
//             const pharmParent = parentSnapshotJson.benefits.find(b => b.id === pharm?.id) ?? parentSnapshotJson.benefits.find(b => b.name === pharm?.name)
//             const medParent = parentSnapshotJson.benefits.find(b => b.id === med?.id) ?? parentSnapshotJson.benefits.find(b => b.name === med?.name)
//             const innrefParent = medParent?.tiers?.find(t => t.bcbs_name === "In Network");
//             if (pharm) {
//                 //DEDUCTIBLE ACCUMULATORS

//                 const field1Attribute = config?.masterattributes?.find(c => c.bcbs_logicalname === "bcbs_accumulators_apply_deductible")?.bcbs_config_masterattributeid;
//                 const field1 = pharm?.fields?.find((f) => f.masterAttributeID === field1Attribute);
//                 const field1Parent = pharmParent?.fields?.find((f) => f.masterAttributeID === field1Attribute);
//                 field1Changed = (field1?.values[0]?.value as IOptionSetValue)?.name !== (field1Parent?.values[0]?.value as IOptionSetValue)?.name;

//                 if (!field1Changed && (field1?.values[0]?.value as IOptionSetValue)?.name === "Medical") {
//                     const field2Attribute = config?.masterattributes?.find(c => c.bcbs_logicalname === "bcbs_mailorder_copay_coins_type")?.bcbs_config_masterattributeid;
//                     const field2 = pharm?.fields?.find((f) => f.masterAttributeID === field2Attribute);
//                     const field2Parent = pharmParent?.fields?.find((f) => f.masterAttributeID === field2Attribute);
//                     field2Changed = ((field2?.values[0]?.value as IOptionSetValue)?.name === "Excluded") !== ((field2Parent?.values[0]?.value as IOptionSetValue)?.name === "Excluded");

//                     const field3Attribute = config?.masterattributes?.find(c => c.bcbs_logicalname === "bcbs_fi_ppo_tfamaggsingletierofnetwork")?.bcbs_config_masterattributeid;
//                     const field3 = med?.fields?.find((f) => f.masterAttributeID === field3Attribute);
//                     const field3Parent = medParent?.fields?.find((f) => f.masterAttributeID === field3Attribute);
//                     field3Changed = (field3?.values?.find(v => v.tierRef?.id === innref?.id)?.value as IStringValue)?.value !== (field3Parent?.values?.find(v => v.tierRef?.id === innrefParent?.id)?.value as IStringValue)?.value;

//                     const field4Attribute = config?.masterattributes?.find(c => c.bcbs_logicalname === "bcbs_fi_ppo_tfamaggfamouttiernetwork")?.bcbs_config_masterattributeid;
//                     const field4 = med?.fields?.find((f) => f.masterAttributeID === field4Attribute);
//                     const field4Parent = medParent?.fields?.find((f) => f.masterAttributeID === field4Attribute);
//                     field4Changed = (field4?.values?.find(v => v.tierRef?.id === innref?.id)?.value as IStringValue)?.value !== (field4Parent?.values?.find(v => v.tierRef?.id === innrefParent?.id)?.value as IStringValue)?.value;

//                     const field5Attribute = config?.masterattributes?.find(c => c.bcbs_logicalname === "bcbs_medicaldeductibletype")?.bcbs_config_masterattributeid;
//                     const field5 = med?.fields?.find((f) => f.masterAttributeID === field5Attribute);
//                     const field5Parent = medParent?.fields?.find((f) => f.masterAttributeID === field5Attribute);
//                     field5Changed = (field5?.values[0]?.value as IOptionSetValue)?.name !== (field5Parent?.values[0]?.value as IOptionSetValue)?.name;
//                 }
//                 else if (!field1Changed && (field1?.values[0]?.value as IOptionSetValue)?.name === "Pharmacy") {
//                     const field6Attribute = config?.masterattributes?.find(c => c.bcbs_logicalname === "bcbs_pharmacydeductibletype")?.bcbs_config_masterattributeid;
//                     const field6 = pharm?.fields?.find((f) => f.masterAttributeID === field6Attribute);
//                     const field6Parent = pharmParent?.fields?.find((f) => f.masterAttributeID === field6Attribute);
//                     field6Changed = (field6?.values[0]?.value as IOptionSetValue)?.name !== (field6Parent?.values[0]?.value as IOptionSetValue)?.name;
//                 }

//                 //OOP ACCUMULATORS
//                 const field7Attribute = config?.masterattributes?.find(c => c.bcbs_logicalname === "bcbs_accumulators_apply_out_of_pocket")?.bcbs_config_masterattributeid;
//                 const field7 = pharm?.fields?.find((f) => f.masterAttributeID === field7Attribute)
//                 const field7Parent = pharmParent?.fields?.find((f) => f.masterAttributeID === field7Attribute);
//                 field7Changed = (field7?.values[0]?.value as IOptionSetValue)?.name !== (field7Parent?.values[0]?.value as IOptionSetValue)?.name;

//                 if (!field7Changed && (field7?.values[0]?.value as IOptionSetValue)?.name === "Medical") {
//                     if (!field2Changed) {
//                         const field2Attribute = config?.masterattributes?.find(c => c.bcbs_logicalname === "bcbs_mailorder_copay_coins_type")?.bcbs_config_masterattributeid;
//                         const field2 = pharm?.fields?.find((f) => f.masterAttributeID === field2Attribute);
//                         const field2Parent = pharmParent?.fields?.find((f) => f.masterAttributeID === field2Attribute);
//                         field2Changed = ((field2?.values[0]?.value as IOptionSetValue)?.name === "Excluded") !== ((field2Parent?.values[0]?.value as IOptionSetValue)?.name === "Excluded");
//                     }
//                     const field8Attribute = config?.masterattributes?.find(c => c.bcbs_logicalname === "bcbs_outofpockettype")?.bcbs_config_masterattributeid;
//                     const field8 = med?.fields?.find((f) => f.masterAttributeID === field8Attribute);
//                     const field8Parent = medParent?.fields?.find((f) => f.masterAttributeID === field8Attribute);
//                     field8Changed = (field8?.values[0]?.value as IOptionSetValue)?.name !== (field8Parent?.values[0]?.value as IOptionSetValue)?.name;
//                     if (!field8Changed && (
//                         (field8?.values[0]?.value as IOptionSetValue)?.name === 'Individual Coinsurance (Indicator 20)' ||
//                         (field8?.values[0]?.value as IOptionSetValue)?.name === 'Individual and Family Combination Coinsurance (Indicator 24)' ||
//                         (field8?.values[0]?.value as IOptionSetValue)?.name === 'Individual and Family Coinsurance Based on Member Coverage Status (Indicator 27)' ||
//                         (field8?.values[0]?.value as IOptionSetValue)?.name === 'Family Coinsurance (Indicator 22)')) {

//                         const field9Attribute = config?.masterattributes?.find(c => c.bcbs_logicalname === "bcbs_medicaldeductibletype")?.bcbs_config_masterattributeid;
//                         const field9 = med?.fields?.find((f) => f.masterAttributeID === field9Attribute);
//                         const field9Parent = medParent?.fields?.find((f) => f.masterAttributeID === field9Attribute);
//                         field9Changed = ((field9?.values[0]?.value as IOptionSetValue)?.name === 'Family Aggregate Deductible (Indicator 27)') !== ((field9Parent?.values[0]?.value as IOptionSetValue)?.name === 'Family Aggregate Deductible (Indicator 27)');
//                     }
//                     const field10Attribute = config?.masterattributes?.find(c => c.bcbs_logicalname === "bcbs_oopglobalpermembermaxtier1")?.bcbs_config_masterattributeid;
//                     const field10 = med?.fields?.find((f) => f.masterAttributeID === field10Attribute)
//                     const field10Parent = medParent?.fields?.find((f) => f.masterAttributeID === field10Attribute)
//                     field10Changed = (field10?.values?.find(v => v.tierRef?.id === innref?.id)?.value as IStringValue)?.value !== (field10Parent?.values?.find(v => v.tierRef?.id === innrefParent?.id)?.value as IStringValue)?.value

//                     if (!field10Changed && !(field10?.values?.find(v => v.tierRef?.id === innref?.id)?.value as IStringValue)?.value) {
//                         const field11Attribute = config?.masterattributes?.find(c => c.bcbs_logicalname === "bcbs_aso_ded_mbr_mon_pb")?.bcbs_config_masterattributeid;
//                         const field11 = pharm?.fields?.find((f) => f.masterAttributeID === field11Attribute);
//                         const field11Parent = pharmParent?.fields?.find((f) => f.masterAttributeID === field11Attribute);
//                         field11Changed = field11?.values[0]?.value !== field11Parent?.values[0]?.value;
//                         const field14Attribute = config?.masterattributes?.find(c => c.bcbs_logicalname === "bcbs_aso_ded_oop_benefit_yr_ded")?.bcbs_config_masterattributeid;
//                         const field14 = pharm?.fields?.find((f) => f.masterAttributeID === field14Attribute);
//                         const field14Parent = pharmParent?.fields?.find((f) => f.masterAttributeID === field14Attribute);
//                         field14Changed = field14?.values[0]?.value !== field14Parent?.values[0]?.value;

//                     }
//                     const field12Attribute = config?.masterattributes?.find(c => c.bcbs_logicalname === "bcbs_oopglobalperfamilytier1")?.bcbs_config_masterattributeid;
//                     const field12 = med?.fields?.find((f) => f.masterAttributeID === field12Attribute)
//                     const field12Parent = medParent?.fields?.find((f) => f.masterAttributeID === field12Attribute)
//                     field12Changed = (field12?.values?.find(v => v.tierRef?.id === innref?.id)?.value as IStringValue)?.value !== (field12Parent?.values?.find(v => v.tierRef?.id === innrefParent?.id)?.value as IStringValue)?.value

//                     if (!field12Changed && !(field12?.values?.find(v => v.tierRef?.id === innref?.id)?.value as IStringValue)?.value) {
//                         const field13Attribute = config?.masterattributes?.find(c => c.bcbs_logicalname === "bcbs_aso_ded_family_mon_pb")?.bcbs_config_masterattributeid;
//                         const field13 = pharm?.fields?.find((f) => f.masterAttributeID === field13Attribute);
//                         const field13Parent = pharmParent?.fields?.find((f) => f.masterAttributeID === field13Attribute);
//                         field13Changed = field13?.values[0]?.value !== field13Parent?.values[0]?.value;
//                     }
//                 }
//                 else if (!field7Changed && (field7?.values[0]?.value as IOptionSetValue)?.name === "Pharmacy") {
//                     if (!field5Changed) {
//                         const field5Attribute = config?.masterattributes?.find(c => c.bcbs_logicalname === "bcbs_medicaldeductibletype")?.bcbs_config_masterattributeid;
//                         const field5 = med?.fields?.find((f) => f.masterAttributeID === field5Attribute);
//                         const field5Parent = medParent?.fields?.find((f) => f.masterAttributeID === field5Attribute);
//                         field5Changed = (field5?.values[0]?.value as IOptionSetValue)?.name !== (field5Parent?.values[0]?.value as IOptionSetValue)?.name;
//                     }
//                     const field15Attribute = config?.masterattributes?.find(c => c.bcbs_logicalname === "bcbs_doesdeductiblefeedoopcode")?.bcbs_config_masterattributeid;
//                     const field15 = pharm?.fields?.find((f) => f.masterAttributeID === field15Attribute);
//                     const field15Parent = pharmParent?.fields?.find((f) => f.masterAttributeID === field15Attribute);
//                     field15Changed = field15?.values[0]?.value !== field15Parent?.values[0]?.value;
//                 }

//                 change = field1Changed || field2Changed || field3Changed || field4Changed || field5Changed || field6Changed
//                     || field7Changed || field8Changed || field9Changed || field10Changed || field11Changed || field12Changed || field13Changed || field14Changed || field15Changed
//             }
//             if (change) {
//                 const planChangeObject = {
//                     planId: plan.id,
//                     planName: plan.name,
//                     linkToORE: `https://app-prod-eastus.grouplibrary.bcbssc.com/editor/pharmacy/${snapshot?._bcbs_groupid_value?.toLowerCase()}/${snapshot?.bcbs_groupconfigurationsnapshotid?.toLowerCase()}/${plan?.id?.toLowerCase()}/${parentSnapshot?.bcbs_groupconfigurationsnapshotid?.toLowerCase()}`,
//                     OREChanges: {
//                         field1Changed: field1Changed,
//                         field2Changed: field2Changed,
//                         field3Changed: field3Changed,
//                         field4Changed: field4Changed,
//                         field5Changed: field5Changed,
//                         field6Changed: field6Changed,
//                         field7Changed: field7Changed,
//                         field8Changed: field8Changed,
//                         field9Changed: field9Changed,
//                         field10Changed: field10Changed,
//                         field11Changed: field11Changed,
//                         field12Changed: field12Changed,
//                         field13Changed: field13Changed,
//                         field14Changed: field14Changed,
//                         field15Changed: field15Changed,

//                     }
//                 };

//                 debugger;
//                 ret.changed = change;
//                 // @ts-ignore
//                 ret.changedPlans.push(planChangeObject);
//             }
//         });

//         return ret;
//     }

//     const result = [] as any[];

//     for (const eachSnapshot of dataSourceJson.goldStandards) {

//         console.log(`⚫ processing ${eachSnapshot.bcbs_groupconfigurationsnapshotid}`)

//         const { snapshot, accountSnapshots } = await _load(eachSnapshot.bcbs_groupconfigurationsnapshotid);

//         // faking used state object:
//         const state: RootState = {
//             status: {
//                 snapshots: accountSnapshots
//             }
//         } as RootState;

//         const parentSnapshot = compareUnderWritingReviewToWithoutDispatch(state, snapshot!);

//         if (parentSnapshot?.bcbs_groupconfigurationsnapshotid)
//             console.log(`⚫ parent snapshot ${parentSnapshot?.bcbs_groupconfigurationsnapshotid}`)
//         else
//             console.log(`🔴 parent snapshot missed.`)

//         // we call the parent detection function here to know which
//         // requires the snapshot and the list of snapshots for the account
//         // compareUnderWritingReviewTo

//         // load json for snapshot and parentSnapshot
//         const snapshotJson = await _loadJson(snapshot);

//         const parentSnapshotJson = parentSnapshot
//             ? await _loadJson(parentSnapshot)
//             : null;
//         const compareResult = _compare(snapshot, parentSnapshot, snapshotJson, parentSnapshotJson);

//         result.push(compareResult);
//         console.log(`🟢 processing complete.`)

//     };

//     const log = JSON.stringify(result);
//     const blob = new Blob([log], { type: 'application/json;charset=utf-8' });

//     saveAs(blob, `compare.pre.json`, { autoBom: true });
// }

const setAttributeVisibilityScope = (attribute: IXrmTypes.bcbs_config_attribute) => {

    // Effective Date/Expiration Date
    // null -> 0
    const effectiveDate = new Date(attribute.bcbs_effectivedate!).getTime();
    const expirationDate = new Date(attribute.bcbs_expirationdate!).getTime();
    const effective_effectiveDate = new Date(attribute.bcbs_effectivedate_effectivedate!).getTime();
    const effective_expirationDate = new Date(attribute.bcbs_expirationdate_effectivedate!).getTime();

    const visibility: IFieldVisibility = {
        effectiveDate: effectiveDate,
        expirationDate: expirationDate,
        effective_effectiveDate: effective_effectiveDate,
        effective_expirationDate: effective_expirationDate,
        fundingType: null,
        erisa: null,
        grandFathered: null,
        lineOfBusiness: null,
    };

    // Funding Types Code Summary:
    attribute.bcbs_fundingtypecode?.split(',').forEach(eachCode => {
        visibility.fundingType = visibility.fundingType ?? {};
        visibility.fundingType[eachCode] = true;
    });

    // ERISA code Summary:
    attribute.bcbs_erisacode?.split(',').forEach(eachCode => {
        visibility.erisa = visibility.erisa ?? {};
        visibility.erisa[eachCode] = true;
    });

    // Grand Fathered Code
    attribute.bcbs_grandfatheredcode?.split(',').forEach(eachCode => {
        visibility.grandFathered = visibility.grandFathered ?? {};
        visibility.grandFathered[eachCode] = true;
    });

    // Line of Business Code
    attribute.bcbs_lineofbusinesscode?.split(',').forEach(eachCode => {
        visibility.lineOfBusiness = visibility.lineOfBusiness ?? {};
        visibility.lineOfBusiness[eachCode] = true;
    });

    //@ts-ignore
    attribute.visibility = visibility;
}

/**
 * Loads the configuration asynchronously.
 *
 * @param dataSource - The data source for retrieving the configuration.
 * @param bcbs_config_release - The release of the BCBS config, if not provided, the latest release will be used.
 * @param instructions - The instructions for loading the snapshot.
 * @returns A promise that resolves to the loaded configuration and bcbs_config_release record used.
 */
export const LoadConfigAsync = async (
    dataSource: AppDataSource,
    bcbs_config_release?: IXrmTypes.bcbs_config_release,
    instructions?: ILoadSnapshotInstructions
) => {

    const t0 = performance.now();

    const bakeConfigAsync = async (config: FAM.IGroupConfigurationConfig) => {

        config.cache = {} as any;
        config.cache.map_bcbs_config_masterattribute_cancellations = {};

        // Test code:
        // config.attributes.find(t => t.bcbs_config_attributeid === '8770b9ab-9b0f-ee11-8f6d-000d3a305861')!.bcbs_effectivedate = undefined;

        // sorting Tabs
        config.segments = config.segments.sort((t1, t2) => t1.bcbs_sortorder! - t2.bcbs_sortorder!);
        config.tabs = config.tabs.sort((t1, t2) => t1.bcbs_sortorder! - t2.bcbs_sortorder!);
        config.sections = config.sections.sort((t1, t2) => t1.bcbs_sortorder! - t2.bcbs_sortorder!);
        config.attributes = config.attributes.sort((t1, t2) => t1.bcbs_sortorder! - t2.bcbs_sortorder!);
        config.rows = config.rows.sort((t1, t2) => t1.bcbs_sortorder! - t2.bcbs_sortorder!);
        config.tierlevels = config.tierlevels.sort((a, b) => { return (a?.bcbs_sortorder ?? 0) - (b?.bcbs_sortorder ?? 0) })
        config.copaycoinoptions.forEach(eachOpt => {
            eachOpt.bcbs_CopayCoinOption_Config_Category = eachOpt.bcbs_CopayCoinOption_Config_Category.sort((t1, t2) => {
                return t1.bcbs_name.localeCompare(t2.bcbs_name);
            });
        });

        // Attribute Updates:
        config.attributes.forEach(eachAttribute => {
            const eachAttributeExtended = eachAttribute as IXrmTypesExtended.Ibcbs_config_attribute_extended

            // Set the visibility scope for the attribute with friendly values
            setAttributeVisibilityScope(eachAttribute);

            // Non Standard Sections
            if (!eachAttributeExtended.NonStandardCustomSections) {
                eachAttributeExtended.NonStandardCustomSections = {};

                config?.attributeNonStandardSections.forEach(eachNonStandardSection => {
                    if (eachNonStandardSection.bcbs_config_attributeid === eachAttributeExtended.bcbs_config_attributeid) {
                        const section = config?.sections.find(q => q.bcbs_config_sectionid === eachNonStandardSection.bcbs_config_sectionid);
                        if (section && eachAttributeExtended.NonStandardCustomSections)
                            eachAttributeExtended.NonStandardCustomSections[eachNonStandardSection.bcbs_config_sectionid!] = section;
                    }
                });
            }
        });

        // Master Attribute Updates:
        config.masterattributes.forEach(eachMasterAttribute => {

            // Cancellation Effective Date fields
            if (eachMasterAttribute.bcbs_name === "Cancellation Effective Date") {
                config.cache.map_bcbs_config_masterattribute_cancellations[eachMasterAttribute.bcbs_config_masterattributeid!] = eachMasterAttribute;
            }
        });

        // lets build some caching for performance
        config.cache.map_bcbs_config_tab = config.tabs.reduce((prev, current) => {
            prev[current.bcbs_config_tabid!] = current;
            return prev;
        }, {} as Record<FAM.TGuid, IXrmTypes.bcbs_config_tab>);

        config.cache.map_bcbs_config_section = config.sections.reduce((prev, current) => {
            prev[current.bcbs_config_sectionid!] = current;
            return prev;
        }, {} as Record<FAM.TGuid, IXrmTypes.bcbs_config_section>);

        config.cache.map_bcbs_config_attribute = config.attributes.reduce((prev, current) => {
            prev[current.bcbs_config_attributeid!] = current;
            return prev;
        }, {} as Record<FAM.TGuid, IXrmTypes.bcbs_config_attribute>);

        config.cache.map_bcbs_config_attribute_by_masterAttributeId = config.attributes.reduce((prev, current) => {
            const t = prev[current._bcbs_config_masterattributeid_value!] ?? [];
            t.push(current);
            prev[current._bcbs_config_masterattributeid_value!] = t;
            return prev;
        }, {} as Record<FAM.TGuid, IXrmTypes.bcbs_config_attribute[]>);

        config.cache.map_bcbs_config_masterattribute = config.masterattributes.reduce((prev, current) => {
            prev[current.bcbs_config_masterattributeid!] = current;
            return prev;
        }, {} as Record<FAM.TGuid, IXrmTypes.bcbs_config_masterattribute>);

        config.cache.map_bcbs_config_category = config.categories.reduce((prev, current) => {
            prev[current.bcbs_config_categoryid!] = current;
            return prev;
        }, {} as Record<FAM.TGuid, IXrmTypes.bcbs_config_category>);

        config.cache.map_bcbs_config_category_named = config.categories.reduce((prev, current) => {
            const name = current.bcbs_name as IXrmTypesExtended.TCategoryName;
            prev[name] = current;
            return prev;
        }, {} as Record<IXrmTypesExtended.TCategoryName, IXrmTypes.bcbs_config_category>);

        config.cache.map_bcbs_copaycoinoption_type = config.copaycoinoptions.reduce((prev, curr) => {
            prev[curr.bcbs_config_categoryid] = curr.bcbs_CopayCoinOption_Config_Category
            return prev;
        }, {} as Record<FAM.TGuid, IXrmTypesExtended.ICopayCoin[]>);

        config.cache.map_bcbs_config_attribute_sets = config.masterattributes.filter(t => t.bcbs_attributetypecode === EControlType.attributeSet)
            .reduce((prev, curr) => {
                const u = config.attributes.filter(w => w._bcbs_masterattributesetid_value === curr.bcbs_config_masterattributeid)
                prev[curr.bcbs_config_masterattributeid!] = u;
                return prev;
            }, {} as Record<FAM.TGuid, IXrmTypes.bcbs_config_attribute[]>)

        config.cache.map_bcbs_config_optionsetitems_bySetId = config.optionsetitems.reduce((prev, current) => {
            const prevOptionSet = prev[current._bcbs_optionsetid_value!] ?? [];
            prev[current._bcbs_optionsetid_value!] = [...prevOptionSet, current];
            return prev;
        }, {} as Record<FAM.TGuid, IXrmTypes.bcbs_config_optionsetitem[]>);

        config.cache.map_bcbs_config_optionsetitems = config.optionsetitems.reduce((prev, current) => {
            prev[current.bcbs_config_optionsetitemid!] = current;
            return prev;
        }, {} as Record<FAM.TGuid, IXrmTypes.bcbs_config_optionsetitem>);

        config.cache.map_bcbs_bccadmin = config.bcbs_bccadmin.reduce((prev, current) => {
            prev[current.bcbs_bccadminid!] = current;
            return prev;
        }, {} as Record<FAM.TGuid, IXrmTypes.bcbs_bccadmin>);

        config.cache.map_bcbs_config_tierlevels_by_tierSetId = config.tierlevels.reduce((prev, curr) => {
            // grab previous tierlevel by tier id and if it doesnt exist create a new one
            const t = prev[curr._bcbs_tiersetid_value!] ?? [];
            t.push(curr);
            prev[curr._bcbs_tiersetid_value!] = t;
            return prev;
        }, {} as Record<FAM.TGuid, IXrmTypes.bcbs_config_tierlevel[]>);

        // Optionset/DropDown mapping
        config.cache.options_bcbs_lob = ConvertToDropdownOptions(config.appOptionSets?.bcbs_lob ?? []);
        config.cache.options_bcbs_fundingtypes = ConvertToDropdownOptions(config.appOptionSets?.bcbs_fundingtypes ?? []);
        config.cache.options_bcbs_erisacode = ConvertToDropdownOptions(config.appOptionSets?.bcbs_erisacode ?? []);
        config.cache.options_bcbs_grandfatheredcode = ConvertToDropdownOptions(config.appOptionSets?.bcbs_grandfatheredcode ?? []);
        config.cache.options_bcbs_changetypecode = ConvertToDropdownOptions(config.appOptionSets?.bcbs_groupconfigurationchangetypecode ?? []);
        config.cache.options_bcbs_idcardorderingoptioncode = ConvertToDropdownOptions(config.appOptionSets?.bcbs_idcardorderingoptioncode ?? []);

        config.cache.bcbs_config_category_benefits = config.categories.filter(q => q._bcbs_config_segmentid_value === '69d33403-a832-ec11-b6e5-0022480ab88f');

        config.cache.options_status = buildDeactivateOptions();
        config.cache.options_benefitStatusReasons = buildBenefitStatusOptions();
        config.cache.options_groupConfigurationStatusReasons = buildGroupConfigurationStatusOptions();
        config.cache.options_products = buildProductOptions();

        config.cache.categoryFormDefinitions = buildCategoryFormDefinitions(config);
        config.cache.attributeSetFormDefinitions = buildAttributeSetFormDefinitions(config);
        config.cache.nonStandardFormDefinitions = buildNonStandardFormDefinitions(config);

        const parentVisibilityGraphProcessor = new ParentVisibilityGraphProcessor(config);
        const graph = parentVisibilityGraphProcessor.initialize(config);

        config.cache.parentVisibilityGraph = graph;

        config.sprintInfo = {
            sprint17: {
                date: new Date('2023-07-05 03:29:26.000Z').getTime(),
                systemVersion: 1027,
                productionVersion: 1027,
            },
            sprint18: {
                date: new Date('2023-07-05 03:29:26.000Z').getTime(),
                systemVersion: 1032,
                productionVersion: 1032,
            },
            sprint21: {
                date: new Date('2023-10-13 22:32:44.000Z').getTime(),
                systemVersion: 1050,
                productionVersion: 1050,
            },
            sprint22: {
                date: new Date('2023-11-07 15:36:00.000Z').getTime(),
                systemVersion: 1054,
                productionVersion: 1054,
            },
            sprint24: {
                date: new Date('2024-01-03 03:56:50.535Z').getTime(),
                systemVersion: 1061,
                productionVersion: 1061,
            },
            sprint30: {
                date: new Date('2024-06-28T15:14:22.102Z').getTime(),
                systemVersion: 1076,
                productionVersion: 1076,
            },
            sprint33: {
                date: new Date('2024-09-28T15:14:22.102Z').getTime(),
                systemVersion: 1091,
                productionVersion: 1083,
            },
            sprint33Patch: {
                date: new Date('2024-10-02T18:51:46.159Z').getTime(),
                systemVersion: 1103,
                productionVersion: 1088,
            },
            // Sprint34: {
            //     date: new Date('2024-10-26T15:14:22.102Z').getTime(),
            //     systemVersion: ,
            //     productionVersion: ,
            // },
        }

        return config;

    }

    const globalOptionSetMap = (globalOptionSetDefinitions: IXrmTypesExtended.IOptionSetDefinition[], name: IXrmGlobalOptions.TOptionSetNames) => {

        const optionset = globalOptionSetDefinitions.find(q => q.Name === name);

        if (!optionset)
            throw new Error(`optionset(${name}) was not found.`);

        return optionset.Options.map(q => {
            const t: IOptionSetValue = {
                type: 'optionset',
                name: q.Label.LocalizedLabels[0].Label,
                value: q.Value
            }

            return t;
        });
    }

    const getGlobalOptionSetDefinitions = async (configJson: FAM.IGroupConfigurationConfig) => {
        if (configJson.globalOptionSetDefinitions?.length < 0)
            return configJson.globalOptionSetDefinitions;

        const globalOptionSetDefinitionsImport = await import("app/GlobalOptionSetDefinitions")
        const { GlobalOptionSetDefinitions } = globalOptionSetDefinitionsImport;
        return GlobalOptionSetDefinitions;
    }

    try {

        if (!bcbs_config_release) {
            console.info("[appi] dataActions.LoadConfigAsync", "bcbs_config_release is not provided, fetching the latest release.");
        }

        bcbs_config_release = bcbs_config_release ?? await dataSource.getPublishedRelease();
        const configJsonFetch = dataSource.downloadConfig(bcbs_config_release.bcbs_config_releaseid!);

        const bcbs_copaycoinoptions_config_categoryFetch = dataSource.getCopayCoinOptions();
        const bcbs_bccadminsFetch = dataSource.getBCCAdmins();

        const [
            configJson,
            bcbs_copaycoinoptions_config_category,
            bcbs_bccadmins
        ] = await Promise.all([
            configJsonFetch,
            bcbs_copaycoinoptions_config_categoryFetch,
            bcbs_bccadminsFetch
        ]);

        let config = {} as FAM.IGroupConfigurationConfig;

        config.segments = configJson.segments;
        config.categories = configJson.categories;
        config.tabs = configJson.tabs;
        config.sections = configJson.sections;
        config.attributes = configJson.attributes;
        config.masterattributes = configJson.masterattributes;
        config.optionsets = configJson.optionsets;
        config.optionsetitems = configJson.optionsetitems;
        config.tiersets = configJson.tiersets;
        config.tierlevels = configJson.tierlevels;
        config.rows = configJson.rows;
        config.attributeNonStandardSections = configJson.attributeNonStandardSections;
        config.copaycoinoptions = bcbs_copaycoinoptions_config_category as any;
        config.globalOptionSetDefinitions = await getGlobalOptionSetDefinitions(configJson)
        config.bcbs_bccadmin = bcbs_bccadmins;

        if (instructions?.liveLoad) {

            const fetchPromises = [
                dataSource.getConfigSegments(),
                dataSource.getConfigCategories(),
                dataSource.getConfigTabs(),
                dataSource.getConfigSections(),
                dataSource.getConfigAttributes(),
                dataSource.getConfigAttributeNonStandardSections(),
                dataSource.getConfigMasterAttributes(),
                dataSource.getConfigOptionSets(),
                dataSource.getConfigOptionSetItems(),
                dataSource.getConfigTierSets(),
                dataSource.getConfigTierLevels(),
                dataSource.getCopayCoinOptions(),
                dataSource.getGlobalOptionSetDefinitions(),
                dataSource.getBCCAdmins(),
                dataSource.getConfigAttributeRows(),
            ];

            const js = await Promise.all<any>(fetchPromises);

            [
                config.segments,
                config.categories,
                config.tabs,
                config.sections,
                config.attributes,
                config.attributeNonStandardSections,
                config.masterattributes,
                config.optionsets,
                config.optionsetitems,
                config.tiersets,
                config.tierlevels,
                config.copaycoinoptions,
                config.globalOptionSetDefinitions,
                config.bcbs_bccadmin,
                config.rows
            ] = js as any;

        }

        // todo this is runtime info, needs to be moved out of here
        config.appOptionSets = {
            bcbs_fundingtypes: globalOptionSetMap(config.globalOptionSetDefinitions, "bcbs_fundingtypes"),
            bcbs_groupconfigurationchangetypecode: globalOptionSetMap(config.globalOptionSetDefinitions, "bcbs_groupconfigurationchangetypecode"),
            bcbs_erisacode: globalOptionSetMap(config.globalOptionSetDefinitions, "bcbs_erisacode"),
            bcbs_lob: globalOptionSetMap(config.globalOptionSetDefinitions, "bcbs_lob"),
            bcbs_grandfatheredcode: globalOptionSetMap(config.globalOptionSetDefinitions, "bcbs_grandfatheredcode"),
            bcbs_idcardorderingoptioncode: globalOptionSetMap(config.globalOptionSetDefinitions, "bcbs_idcardorderingoptioncode"),
        }

        config.bcbs_config_release = bcbs_config_release;

        // test code
        // config.masterattributes.find(t => t.bcbs_config_masterattributeid === '38a09dd0-58d0-ee11-907a-000d3a3bec27')!.bcbs_attributetypecode = EControlType.multiOptionset;

        config = await bakeConfigAsync(config);

        console.log(`[appl] dataActions.LoadConfigAsync executiontime ${performance.now() - t0}ms.`, config);

        return config;

    } catch (error: any) {
        throw error;
    }
}

export const fetch_account_snapshots_Action = (dataSource: AppDataSource, accountId: string) => {
    return async function fetch_snapshots(dispatch: Dispatch<any>, state: () => RootState) {
        dispatch(startSnapshotLoad());
        const data = await dataSource.getGroupConfigurationSnapshotsForAccount(accountId);
        dispatch(loadSnapshotsData(data));
        dispatch(endSnapshotLoad());
    }
}
/*
export const fetchLeftCompareSnapshotWithTransformation = (dataSource: AppDataSource, accountId: string, options?: ILoadSnapshotOptions) => {
    return async function fetchTransformed(dispatch: any, getState: () => RootState) {
        dispatch(loadSnapshotAction(dataSource, accountId!, options))
        debugger;
        var comp = getState().comp;
        // after left loads, transform it - only called if right already exists
        dispatch(transformLeftSnapForCompare({leftSnapshot: getState().comp.leftSnap, rightSnapshot: getState().comp.rightSnap}))
    }
}
 
export const fetchRightCompareSnapshotWithTransformation = (dataSource: AppDataSource, accountId: string, options?: ILoadSnapshotOptions) => {
    return async function fetchTransformed(dispatch: any, getState: () => RootState) {
        dispatch(loadSnapshotAction(dataSource, accountId!, options))
        // after right loads, transform left again - only called if left already exists
        debugger;
        var comp = getState().comp;
 
        dispatch(transformLeftSnapForCompare({
            leftSnapshot: getState().comp.leftSnap,
            rightSnapshot: getState().comp.rightSnap
        }))
    }
 
}
 */

export const updatePharmacyInformationAction = (ds: AppDataSource, pharm: IPharmacyORE[]) => {
    return async function updatePharmacy(dispatch: Dispatch<any>, state: () => RootState) {
        dispatch(updatePharmacyStatus(pharm));
        const _state = state();
        debugger;

        try {

            dispatch(setProcessing(true));

            let compState: RootState = {
                comp: _state.comp,
                data: {
                    json: _state.comp.rightSnap.json,
                    config: _state.comp.rightSnap.config,
                    snapshot: _state.comp.rightSnap.snapshot
                },
                router: _state.router,
                status: _state.comp.rightSnap.status
            };

            const saveResult = await SaveAsync(dispatch, ds, compState, { onSaveStatusScreen: true });

            const postSaveData: ICompareGroupConfigurationData = {
                ..._state.comp.rightSnap,
                snapshot: saveResult.snapshot!,
                status: {
                    ..._state.comp.rightSnap.status,
                    snapshotBlobVersion: saveResult.newBlobVersion!,
                }
            };

            dispatch(loadRightSnap(postSaveData));

        } catch (error) {

            alert('Encountered exception update pharmacy information...');

            // lets create a new one is payload is not providing one
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });

            throw error

        }
        finally {
            dispatch(setProcessing(false));

        }

    }
}

export const saveMergeAction = (ds: AppDataSource) => {
    return async function (dispatch: Dispatch<any>, state: () => RootState) {

        try {
            const rootState = state();

            const newDataState: IGCData = {
                json: rootState.comp.mergeSnap.json,
                config: rootState.comp.mergeSnap.config,
                snapshot: structuredClone(rootState.comp.mergeSnap.snapshot),
                account: rootState.comp.mergeSnap.status.account!,
            }

            const changeType = IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Change(808900004)"];

            newDataState.snapshot._bcbs_originatingsnapshotid_value = rootState.comp.rightSnap.snapshot.bcbs_groupconfigurationsnapshotid;
            newDataState.snapshot._bcbs_originatingsnapshot2id_value = rootState.comp.leftSnap.snapshot.bcbs_groupconfigurationsnapshotid;
            newDataState.snapshot.bcbs_changetypecode = changeType.value;
            newDataState.snapshot.statuscode = IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value;
            newDataState.snapshot.statecode = bcbs_statecode_byStatus[newDataState.snapshot.statuscode];

            dispatch(updateAccount(rootState.comp.mergeSnap.status.account));
            dispatch(updateSnapshotData(newDataState));
            dispatch(updateSnapshotBlobVersion(''));

            dispatch(addUpdateGroupConfiguration({
                groupConfigurationChanges: {
                    changeType: {
                        name: changeType.name,
                        type: 'optionset',
                        value: changeType.value
                    }
                },
            }));

            // dispatch data fix for these        mergeRootState.data.snapshot!._bcbs_originatingsnapshotid_value = state.comp.rightSnap.snapshot.bcbs_groupconfigurationsnapshotid;

            let instructions: ISaveInstruction = {
                isMergeSave: true,
                onSaveStatusScreen: true,
                onPostSaveIgnoreReload: true,
            };

            let saveResult = await SaveAsync(dispatch, ds, state(), instructions);

            const updateMergeParent = async (localGCD: ICompareGroupConfigurationData) => {

                // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/244683
                // If parent is In Progress, Data Entry Complete, Pending Corrections, Pending Underwriting, MNIL Data Entry Complete --->
                // set the status Merged and Closed

                if (localGCD.snapshot.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value ||
                    localGCD.snapshot.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Data Entry Complete(808900000)'].value ||
                    localGCD.snapshot.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Corrections Pending(808900005)'].value ||
                    localGCD.snapshot.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Pending Underwriting(808900009)'].value ||
                    localGCD.snapshot.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['MNIL Data Entry Complete(808900003)'].value) {

                    let instructions: ISaveInstruction = {
                        onSaveSetStatusCode: IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Merged and Closed(808900010)'].value,
                        onPostSaveIgnoreReload: true,
                        onSaveStatusScreen: true,
                    };

                    let localRootState = {
                        data: {
                            // TODO maybe better to ignore saving snapshot json
                            json: localGCD.json,
                            config: localGCD.config,
                            snapshot: structuredClone(localGCD.snapshot),
                        },
                        status: {
                            account: localGCD.status.account,
                            snapshotBlobVersion: localGCD.status.snapshotBlobVersion,
                            debug: rootState.status.debug
                        } as IGroupConfigurationStatus
                    } as RootState;

                    // if above is causing issues, use this oneliner :
                    // problem is some sub processes need to be updated as well
                    // {
                    // const change: Partial<IXrmTypes.bcbs_groupconfigurationsnapshot> = {};
                    // change.statuscode = bcbs_groupconfigurationsnapshot.statuscode['Merged and Closed(808900010)'].value;
                    // change.statecode = bcbs_statecode_byStatus[change.statuscode];
                    // await ds.updateGroupConfigurationSnapshot(localGCD.snapshot.bcbs_groupconfigurationsnapshotid!, change);
                    // }

                    return await SaveAsync(dispatch, ds, localRootState, instructions);
                }
            }

            await updateMergeParent(rootState.comp.rightSnap);
            await updateMergeParent(rootState.comp.leftSnap);

            const url = `/editor/redirect?reason=view-config&accountId=${saveResult.snapshot?._bcbs_groupid_value!}&snapshotId=${saveResult.snapshot?.bcbs_groupconfigurationsnapshotid}`;
            window.location.href = url;

        } catch (error) {

            alert('Encountered exception during merge...');

            // lets create a new one is payload is not providing one
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });

            throw error;
        }
        finally {

        }

    }
}

export const SaveAsync = async (
    dispatch: Dispatch<any>,
    dataSource: AppDataSource,
    state: RootState,
    xInstructions?: ISaveInstruction
): Promise<ISaveResult> => {

    dispatch(updateDataSavingStatus(ELoadStatus.criticalSaving));

    const newInstructions: ISaveInstruction = { ...xInstructions };

    console.log('[appl] dataAction.SaveAsync instructions', newInstructions);

    appInsights?.trackEvent({
        name: 'SaveAsync',
        properties: {
            bcbs_groupconfigurationsnapshotid: state.data.snapshot.bcbs_groupconfigurationsnapshotid,
            instructions: newInstructions
        }
    });

    let saveResult: ISaveResult = {};

    try {

        if (newInstructions.onSaveStatusScreen) {
            dispatch(startSnapshotLoad());
        }

        let snapshot = structuredClone(state.data.snapshot)!;
        const xSnapshot = snapshot;

        let snapshotBlobVersion = state.status.snapshotBlobVersion!;
        let cloneResult: ICloneResult | null = null;

        if (
            newInstructions.isMergeSave ||
            newInstructions.onCloneSetOriginatingSnapshotId ||
            newInstructions.onSaveClone ||
            newInstructions.saveType === ESaveType.EConcurrencyConflict ||
            newInstructions.saveType === ESaveType.EUpgradeRelease ||
            state.status.debug.clone
        ) {
            // replacing snapshot will cause the following save process to use the new snapshot and will reload
            cloneResult = await CloneSnapshotAsync(dataSource, snapshot, state, newInstructions);

            // movin forward use this snapshot
            snapshot = cloneResult.snapshot;

            snapshotBlobVersion = '';
        }

        // If cloning due to upgrading release, lets close current snapshot
        if (cloneResult && cloneResult.snapshot) {
            if (newInstructions.saveType === ESaveType.EUpgradeRelease) {

                const saveInfo: ISnapshotInfo = {
                    fileName: '',
                    blobVersion: '',
                    json: null
                };

                let changes: Partial<IXrmTypesExtended.IUpdatable_bcbs_groupconfigurationsnapshot> = {
                    bcbs_groupconfigurationsnapshotid: xSnapshot.bcbs_groupconfigurationsnapshotid,
                    statecode: IXrmOptions.bcbs_groupconfigurationsnapshot.statecode['Inactive(1)'].value,
                    statuscode: IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Inactive(808900002)'].value,
                }

                // TODO move notes here

                const saveResult = await dataSource.save(saveInfo, changes, xSnapshot.versionnumber);
                console.info("Inactivated previous snapshot", saveResult)
            }
        }

        if (!snapshot?.bcbs_groupconfigurationsnapshotid)
            throw new Error("Unexpected flow, group configuration snapshot was not available.");

        if (state.data.json?.iteration === null || state.data.json?.iteration === undefined)
            throw new Error("Unexpected flow, snapshot json data was not available.");

        let snapshotChanges: IXrmTypesExtended.IUpdatable_bcbs_groupconfigurationsnapshot = {
            bcbs_groupconfigurationsnapshotid: snapshot.bcbs_groupconfigurationsnapshotid
        };

        // fundingType - delta - CRM loaded group configuration and local snapshot
        // --------------------------------------------------------------------
        if (state.data.json.fundingType?.value)
            if (state.data.snapshot?.bcbs_fundingtypecode !== state.data.json.fundingType.value)
                snapshotChanges.bcbs_fundingtypecode = state.data.json.fundingType?.value

        // changeType - delta - CRM loaded group configuration and local snapshot
        // --------------------------------------------------------------------
        if (state.data.json.changeType?.value)
            if (state.data.snapshot?.bcbs_changetypecode !== state.data.json.changeType.value)
                snapshotChanges.bcbs_changetypecode = state.data.json.changeType.value;

        // effectiveDate 
        // --------------------------------------------------------------------
        // - CRM loaded group configuration and local snapshot
        // * name fields depends on this
        if (state.data.json.effectiveDate?.value)
            if (!state.data.snapshot?.bcbs_effectivedate) {
                // has no value
                snapshotChanges.bcbs_effectivedate = state.data.json.effectiveDate.value;
            }
            else if (state.data.snapshot?.bcbs_effectivedate !== state.data.json.effectiveDate.value) {
                // string values are not the same? lets compare date values
                const _date1 = new Date(state.data.snapshot.bcbs_effectivedate)
                const _date2 = new Date(state.data.json.effectiveDate.value)

                if (_date1.getTime() !== _date2.getTime())
                    snapshotChanges.bcbs_effectivedate = state.data.json.effectiveDate.value;
            }


        // bcbs_operativedate
        // --------------------------------------------------------------------
        // no delta, its not stored on DATA
        // if cloned, we dont update the snapshot operative date


        // bcbs_isreconciliation
        // --------------------------------------------------------------------
        if (hasValue(state.data.json.reconciliation?.value)) {
            if (state.data.snapshot?.bcbs_isreconciliation !== state.data.json.reconciliation?.value) {
                snapshotChanges.bcbs_isreconciliation = state.data.json.reconciliation?.value;
            }
        }

        // Status
        // --------------------------------------------------------------------
        // no delta, its not stored on DATA


        // bcbs_name
        // --------------------------------------------------------------------
        // recalculating the name
        const effectiveDate = snapshotChanges.bcbs_effectivedate ?? snapshot.bcbs_effectivedate ?? null;

        const changeType = newInstructions.isMergeSave
            ? IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Change(808900004)"].name
            : IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode[(state.data.json.changeType?.value! as keyof typeof IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode)].name;

        // example: Group Name | Effective Date mm/dd/yyyy | Change Type | Created ON (mm/dd/yyyy)
        let bcbs_name_calculated = `${state.data.account?.name ?? state.status.account?.name} | ${effectiveDate ? formatDate(parseDate(effectiveDate, 'yyyy-MM-ddThh:mm:ssZ as local'), 'local:MM/dd/yyyy') : '-'} | ${changeType} | ${formatDate(new Date(snapshot.createdon!), 'en-US-ET')}`;

        if (newInstructions.onSaveSetSnapshotName)
            bcbs_name_calculated = newInstructions.onSaveSetSnapshotName

        // if calculated name is different from the record name, patch
        if (snapshot.bcbs_name !== bcbs_name_calculated)
            snapshotChanges.bcbs_name = bcbs_name_calculated;

        // bcbs_shortname
        // --------------------------------------------------------------------
        if (snapshot.bcbs_shortname !== state.data.json.bcbs_shortname)
            snapshotChanges.bcbs_shortname = state.data.json.bcbs_shortname;

        // bcbs_ismnil
        // --------------------------------------------------------------------
        if (!!snapshot.bcbs_ismnil !== (!!state.data.json.MNIL?.value)) {
            snapshotChanges.bcbs_ismnil = (!!state.data.json.MNIL?.value);
        }

        // bcbs_isrevision
        // --------------------------------------------------------------------
        if (!!snapshot.bcbs_isrevision !== (!!state.data.json.isRevision?.value)) {
            snapshotChanges.bcbs_isrevision = (!!state.data.json.isRevision?.value);
        }

        // bcbs_opportunity
        // --------------------------------------------------------------------
        if (snapshot._bcbs_opportunityid_value !== (state.data.json?.opportunity?.id ?? null))
            if (state.data.json?.opportunity?.id)
                snapshotChanges['bcbs_Opportunityid@odata.bind'] = `/opportunities(${state.data.json.opportunity.id!})`;
            else
                snapshotChanges['bcbs_Opportunityid@odata.bind'] = null;

        // bcbs_benefitcategorylist
        // lets grab all distinct benefit category list
        // --------------------------------------------------------------------
        const benefitList = {} as Record<string, boolean>;

        state.data.json.benefits.forEach(benefit => {
            benefitList[benefit.bcbs_name!] = true;
        });

        const bcbs_benefitcategorylist = Object.keys(benefitList).sort().join(',');

        if (snapshot.bcbs_benefitcategorylist !== bcbs_benefitcategorylist)
            snapshotChanges.bcbs_benefitcategorylist = bcbs_benefitcategorylist;

        // Emit Events
        // --------------------------------------------------------------------
        // Benefit-Status-Level

        //(lookup) Group Configuration Snapshot
        // POC code, probably will change entirely
        //TODO probably will send all this to appservice api, which then it will queue to azure service bus which then function app takes over                        
        await EmitEventProcess(dataSource);

        // status: Force change status code and following state code
        // --------------------------------------------------------------------
        if (state?.data?.json?.status?.value === FAM.EGroupConfigurationStatus.Cancelled) {
            if (snapshot.statuscode !== IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Cancelled(808900011)'].value) {
                snapshotChanges.statuscode = IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Cancelled(808900011)'].value;
            }
        }

        if (state?.data?.json?.status?.value === FAM.EGroupConfigurationStatus['Cancellation in Progress']) {
            if (snapshot.statuscode !== IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Cancellation in Progress(808900013)'].value) {
                snapshotChanges.statuscode = IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Cancellation in Progress(808900013)'].value;
            }
        }

        if (state?.data?.json?.status?.value === FAM.EGroupConfigurationStatus['Transferred to Other LOB']) {
            if (snapshot.statuscode !== IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Transferred to Other LOB(808900012)'].value) {
                snapshotChanges.statuscode = IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Transferred to Other LOB(808900012)'].value;
            }
        }

        if (state?.data?.json?.status?.value === FAM.EGroupConfigurationStatus['Transferred to Other LOB in Progress']) {
            if (snapshot.statuscode !== IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Transfer to Other LOB in Progress(808900014)'].value) {
                snapshotChanges.statuscode = IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Transfer to Other LOB in Progress(808900014)'].value;
            }
        }

        // statuscode
        // --------------------------------------------------------------------
        // TODO move this out of here, to a admin menu as a validation save item
        if (newInstructions.onSaveForceStatusCodeToInProgress) {
            snapshotChanges.statuscode = IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value;
        }

        if (newInstructions.onSaveSetStatusCode) {
            if (snapshot.statuscode !== newInstructions.onSaveSetStatusCode) {

                snapshotChanges.statuscode = newInstructions.onSaveSetStatusCode;

                // when a GC goes to Data Entry Complete, the Data Entry Complete date is needs to be set (always).
                if (snapshotChanges.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Data Entry Complete(808900000)'].value) {
                    snapshotChanges.bcbs_dataentrycompletedate = (new Date()).toISOString();
                }

                if (snapshotChanges.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Underwriting Review Complete(2)'].value ||
                    snapshotChanges.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['MNIL Data Entry Complete(808900003)'].value) {
                    snapshotChanges.bcbs_underwritingreviewcompletedate = (new Date()).toISOString();
                }
            }
        }

        // statecode
        // --------------------------------------------------------------------
        // lets map status code to state code
        if (snapshotChanges.statuscode) {
            snapshotChanges.statecode = bcbs_statecode_byStatus[snapshotChanges.statuscode];
        }

        // bcbs_isbypassuwandtasks
        // --------------------------------------------------------------------
        if (newInstructions.onSaveBypassUnderwritingTaskGeneration) {
            if (!snapshot.bcbs_isbypassuwandtasks)
                snapshotChanges.bcbs_isbypassuwandtasks = true;
        }

        // bcbs_UWConfirmedParentid
        // --------------------------------------------------------------------
        if (newInstructions.onSaveChangeUWConfirmedParentId) {
            if (!snapshot._bcbs_uwconfirmedparentid_value)
                snapshotChanges["bcbs_UWConfirmedParentid@odata.bind"] = "bcbs_groupconfigurationsnapshots(" + newInstructions.onSaveChangeUWConfirmedParentId + ")";
        }

        // bcbs_appurl
        // --------------------------------------------------------------------
        const bcbs_appurl = `${Constants.app.groupLibraryURL}/editor/redirect?reason=view-config&accountId=${snapshot?._bcbs_groupid_value!}&snapshotId=${snapshot?.bcbs_groupconfigurationsnapshotid}`
        if (snapshot.bcbs_appurl !== bcbs_appurl)
            snapshotChanges.bcbs_appurl = bcbs_appurl;

        // bcbs_config_releaseid
        // --------------------------------------------------------------------
        if (newInstructions.onSaveUpdateRelease) {
            const publishedRelease = await dataSource.getPublishedRelease();

            if (snapshot._bcbs_releaseid_value !== publishedRelease.bcbs_config_releaseid)
                snapshotChanges["bcbs_Releaseid@odata.bind"] = `/bcbs_config_releases(${publishedRelease.bcbs_config_releaseid})`;

            // TODO if release gets updated, rerun whole transfomers
        }

        // bcbs_enrollmentdetailsid 
        // --------------------------------------------------------------------
        // *** care on updating dependencies beyond this point
        // example : https://localhost:44350/editor/redirect?reason=new-config&accountId=39c594d3-62d0-ea11-a812-000d3a3abb8a&snapshotId=0fa4147a-7baf-ec11-9840-000d3a3573b3
        // --------------------------------------------------------------------
        // not used at the moment
        // place holder for future if we need to save in single transaction
        if (newInstructions.onSaveCreateEnrollmentDetail) {
            // is it a brand new enrollment detail ? lets create and link it to snapshot

            // no enrollment data has been assoicated
            if (!snapshot._bcbs_enrollmentdetailsid_value) {

                const enrollmentDetail = await dataSource.createAndReturnEnrollmentDetail(newInstructions.onSaveCreateEnrollmentDetail);
                snapshotChanges['bcbs_EnrollmentDetailsid@odata.bind'] = `/bcbs_enrollmentdetailses(${enrollmentDetail.bcbs_enrollmentdetailsid})`;
            }
        }

        // bcbs_filename
        // --------------------------------------------------------------------
        // *** we only want to regenerate this if it's empty
        const bcbs_filename = dataSource.generateFileNameFromSnapshot(snapshot);
        if (!snapshot.bcbs_filename)
            snapshot.bcbs_filename = snapshotChanges.bcbs_filename = bcbs_filename;

        // Merge
        // --------------------------------------------------------------------
        // if it's a merge operation, we only need to update a few fields
        if (newInstructions.isMergeSave) {
            let overrideChange: typeof snapshotChanges = {};

            copyPropertiesOnly(
                snapshotChanges,
                overrideChange,
                [
                    'bcbs_groupconfigurationsnapshotid',
                    'bcbs_name',
                    'bcbs_appurl',
                    'bcbs_filename',
                    'bcbs_isreconciliation'
                ],
                true
            );

            snapshotChanges = overrideChange;
        }

        if (snapshotChanges.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Underwriting Review Complete(2)'].value ||
            snapshotChanges.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['MNIL Data Entry Complete(808900003)'].value) {

            for (const eachRevisionTask of state.data.json.revisionTasks) {
                await dataSource.revisionTaskUpdate(eachRevisionTask.id, {
                    'bcbs_RevisionSnapshotid_Task@odata.bind': `/bcbs_groupconfigurationsnapshots(${snapshot.bcbs_groupconfigurationsnapshotid})`,
                });

                console.log("updating task", eachRevisionTask.id);
            }
        }

        let json = state.data.json;

        // applying updates to newJson
        if (cloneResult) {

            // making a local nonproxy object
            json = structuredClone(state.data?.json);

            snapshot = { ...snapshot, ...snapshotChanges };

            const cloneTransformers: IJsonTransformer[] = [];

            // if upgrading release, dont make modifications to deactivated entities
            // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/409332

            if (newInstructions.saveType !== ESaveType.EUpgradeRelease)
                cloneTransformers.push(new RemoveDeactivatedDataTransformer());

            if (newInstructions.saveType !== ESaveType.EUpgradeRelease)
                cloneTransformers.push(new PharmacyResetTransformer());

            if (newInstructions.saveType !== ESaveType.EUpgradeRelease)
                cloneTransformers.push(new CloneTransformer(state.data.config, snapshot, true, newInstructions ?? null));

            if (newInstructions.saveType !== ESaveType.EUpgradeRelease)
                cloneTransformers.push(new RevisionResetTransformer(newInstructions));

            // if upgrading release
            if (newInstructions.saveType === ESaveType.EUpgradeRelease)
                cloneTransformers.push(new UpgradeReleaseTransformer(newInstructions, xSnapshot, snapshot));

            const transform = new JsonTransformer(cloneTransformers, EJsonTransformerRuntime.ESequential);

            if (cloneTransformers.length > 0)
                await transform.transformAsync(json, snapshot);

            // wait for clones to flush
            await EmitEventProcess(dataSource);
        }

        // Excpetion bcbs_name + bcbs_shortname
        // --------------------------------------------------------------------
        // prevent name change for gc under template account (product templates)
        // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=account&id=cb78766c-4ed5-ec11-a7b5-000d3a3ab609
        if (state.data.account?.accountid === 'cb78766c-4ed5-ec11-a7b5-000d3a3ab609') {

            if (snapshotChanges.bcbs_name) {
                console.warn("bcbs_name update has been prevented on template record")
                delete snapshotChanges.bcbs_name;
            }

            if (snapshotChanges.bcbs_shortname) {
                console.warn("bcbs_name update has been prevented on template record")
                delete snapshotChanges.bcbs_shortname;
            }
        }

        const saveInfo: ISnapshotInfo = {
            fileName: snapshot.bcbs_filename!,
            blobVersion: snapshotBlobVersion,
            json: json
                ? json
                : null
        };

        if (newInstructions.ignoreJsonSave) {
            saveInfo.json = null;
        }

        saveResult = await dataSource.save(saveInfo, snapshotChanges, snapshot.versionnumber);
        saveResult.instructions = newInstructions;

        if (newInstructions.onSaveGenerateGoldStandardAndSQL) {

            const processes: Promise<void>[] = []
            // lets create gold standard:
            processes.push(dataSource.createProcesses({
                bcbs_recordid: saveResult.snapshot?.bcbs_groupconfigurationsnapshotid,
                bcbs_name: `${saveResult.snapshot?.bcbs_name} | Gold Standard Record Generation(M)`,
                bcbs_triggerreason: `bcbs_gsrecordgenerationurl`
            }));


            // lets create SQL:
            processes.push(dataSource.createProcesses({
                bcbs_recordid: saveResult.snapshot?.bcbs_groupconfigurationsnapshotid,
                bcbs_name: `${saveResult.snapshot?.bcbs_name} | SQL Record Generation(M)`,
                bcbs_triggerreason: `bcbs_sqlrecordgenerationurl`
            }));


            // lets create BDR:
            processes.push(dataSource.createProcesses({
                bcbs_recordid: saveResult.snapshot?.bcbs_groupconfigurationsnapshotid,
                bcbs_name: `${saveResult.snapshot?.bcbs_name} | BDR Generation(M)`,
                bcbs_triggerreason: `bcbs_autogeneratebdr`
            }));

            // lets create Benefit Detail Extract:
            processes.push(dataSource.createProcesses({
                bcbs_recordid: saveResult.snapshot?.bcbs_groupconfigurationsnapshotid,
                bcbs_name: `${saveResult.snapshot?.bcbs_name} | Benefit Detail Extract Generation(M)`,
                bcbs_triggerreason: `bcbs_autogeneratebenefitdetailextract`
            }));

            await Promise.all(processes);
        }

        dispatch(setSaveTokenOnData());

        // TODO need to talk to William Bittle about this.
        // I think it makes sense to change the arch a little to (its time...)
        // what he was suggesting here, make the responsiblity of loader to call these

        // for merge, we don't need to update the follow because merge doesn't set these initially
        // setting these will cause the BlueRelationship.tsx to fail when redirecting to the merged
        // snapshot

        // reloading snapshot record ?
        if (!newInstructions.onPostSaveIgnoreReload) {
            dispatch(updateSnapshotData({ snapshot: saveResult.snapshot }));
            dispatch(updateSnapshotBlobVersion(saveResult.newBlobVersion));
            dispatch(updateLastSaved());
        }

        // reloading json ?
        if (json && json !== state.data?.json)
            dispatch(updateSnapshotData({ json: json }));

        if (state.status.debug.exception)
            throw new Error("Testing Exception.");

        if (newInstructions.onPostSaveSetViewOnly)
            dispatch(setViewType(EViewType.ViewOnly));

        dispatch(updateDataSavingStatus(ELoadStatus.complete));

        // Cloning for debug ?
        if (state.status.debug.clone) {

            // open the new one
            (window as Window).open(AppLinkToExternal({
                id: saveResult.snapshot?.bcbs_groupconfigurationsnapshotid!,
                type: 'bcbs_groupconfigurationsnapshot'
            }));

            // lets turn of clone
            dispatch(updateDebug({ clone: false }));

            dispatch(setViewType(EViewType.ViewOnly));
        }

    } catch (err: any) {
        // we have the possibility to get two error types. One error type from BLOB storage and one from CRM.
        // check for the following error codes https://docs.microsoft.com/en-us/rest/api/storageservices/blob-service-error-codes
        // use err.code === 'LeaseAlreadyPresent', 'ConditionNotMet', 'BlobAlreadyExists' - caused by BLOB
        // use err.status === 412 - caused by the if-match optimistic concurrency failed on CRM
        if (err.code === 'LeaseAlreadyPresent' || // if someone is saving at the same time as you
            err.code === 'ConditionNotMet' || // if someone has saved on top of you
            err.code === 'BlobAlreadyExists' || // if someone has created the blob before you
            err.status === 412) { // someone has saved the CRM record on top of you

            // TODO need to talk to William Bittle about this.
            // TODO this handler needs to get out of here

            // all of these errors indicate an optimistic concurrency issue
            const snapshots = await dataSource.getGroupConfigurationSnapshotsForAccount(state?.data?.snapshot?._bcbs_groupid_value!);
            const snap = snapshots.find(s => s.bcbs_groupconfigurationsnapshotid === state?.data?.snapshot?.bcbs_groupconfigurationsnapshotid);
            const user = await dataSource.crm.getRecord<IXrmTypes.systemuser>('systemusers', trimGUIDLower(snap?._modifiedby_value!), 'fullname');
            dispatch(setConcurrencyConfirmationModal({ fullname: user.fullname ?? '', modifiedon: snap?.modifiedon! }));
            saveResult.snapshot = snap;
            return saveResult;
        }

        alert('saving failed.');

        // lets create a new one is payload is not providing one
        emitCustomEvent({
            eventType: 'globalException',
            details: err
        });

        throw err;
    } finally {

        if (newInstructions?.onSaveStatusScreen)
            dispatch(endSnapshotLoad());

        // enough time for beacon logs to flush (if any)
        setTimeout(() => {
            dispatch(updateDataSavingStatus(ELoadStatus.idle));
        }, 2000);

        return saveResult;
    }
}

export const SaveAction = (
    dataSource: AppDataSource,
    history: any,
    instructions: ISaveInstruction,
) => {
    return async function (dispatch: Dispatch<any>, state: () => RootState) {

        const _instructions = { ...instructions };

        console.log('[appl] dataActions.SaveAction');

        try {

            if (document.activeElement)
                (document.activeElement as any).blur();

            // giving priority to any setTimeouts on the event loop to complete if modifying data probably 0 should also work, since it will queue
            await Delay(100);

            // any data dispatches in the queue prior to save?
            if (instructions.onPreSaveUpdateJson)
                dispatch(addUpdateGroupConfiguration(instructions.onPreSaveUpdateJson));

            // lets get the latest state
            const xState = state();

            // if already saving ?
            if (xState.status.dataSavingStatus !== ELoadStatus.idle) {
                console.warn('[appl] dataActions.SaveAction eject', 'pending');
                return;
            }

            // if not in modify view ?
            if (xState.status.viewType !== EViewType.Modify) {
                console.warn('[appl] dataActions.SaveAction eject', 'not-modfying');
                return;
            }

            if (_instructions?.saveType === ESaveType.EAuto) {
                if ((xState.data.savedOn ?? 0) > (xState.data.modifiedOn ?? -1)) {
                    // dirty changes?
                    console.warn('[appl] dataActions.SaveAction eject', 'no-dirty-change');
                    return;
                }
            }

            // latest redux state
            const saveResult = await SaveAsync(dispatch, dataSource, xState, _instructions);

            // not needed, but this gives room for the ui updates to complete
            await Delay(0);

            dispatch(updateCreatingStatus(false))

            if (saveResult.instructions?.onPostSaveSetViewOnly) {
                dispatch(setViewType(EViewType.ViewOnly));
            }

            if (_instructions.onSaveSetStatusCode && _instructions.saveType !== ESaveType.ECreate) {
                dispatch(setViewType(EViewType.ViewOnly));
                AppNavigateToExternal(history, xState, "finished");
            }

            if (_instructions.saveType === ESaveType.EUpgradeRelease || _instructions.onPostSaveNavigateExternalToSaveResult) {
                dispatch(setViewType(EViewType.ViewOnly));
                AppNavigateToExternal(history, xState, "finished");
                // const win: Window = window;
                // win.location = (`/records/blueRelationship/${xState.status.account?.accountid!}/groupConfiguration/${t.snapshot?.bcbs_groupconfigurationsnapshotid}?entry=modify`)
                window.open(`/records/blueRelationship/${xState.status.account?.accountid!}/groupConfiguration/${saveResult.snapshot?.bcbs_groupconfigurationsnapshotid}`);
            }

        } catch (error) {

            if (error instanceof ValidationError) {
                // error is expected in case of validation didnt go through
            }
            else {

                emitCustomEvent({
                    eventType: 'globalException',
                    details: error
                });

                throw error;
            }
        } finally {

        }

    }
}

export const approveUnderwritingAction = (ds: AppDataSource, history: any, approve: boolean) => {
    return async function (dispatch: Dispatch<any>, state: () => RootState) {
        let _state = state();
        let compState: RootState = {
            comp: _state.comp,
            data: {
                json: _state.comp.rightSnap.json,
                config: _state.comp.rightSnap.config,
                snapshot: _state.comp.rightSnap.snapshot
            },
            router: _state.router,
            status: _state.comp.rightSnap.status
        };

        const saveInstruction: ISaveInstruction = {
            onSaveSetStatusCode: IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Underwriting Review Complete(2)'].value,
            onSaveStatusScreen: true,
            isUnderwritingSave: true,
        }
        if (_state.comp.rightSnap.snapshot.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['MNIL Data Entry Complete(808900003)'].value) {
            saveInstruction.onSaveSetStatusCode = IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['MNIL Underwriting Complete(808900004)'].value
        }
        if (!approve) {
            saveInstruction.onSaveSetStatusCode = IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Corrections Pending(808900005)'].value;
        }

        await SaveAsync(dispatch, ds, compState, saveInstruction);

        // todo clear state
        AppNavigateToExternal(history, _state, "finished");
    }
}

export const entryDialogConfirmAction = (
    mode: 'view' | 'modify' | 'reopen and modify' | 'create' | 'underwriting review',
    reopenPayload?: {
        dataSource: any,
        state: any,
    },
    underwritingPayload?: {
        state: any,
        snapshot: any,
        history: any,
        accountId: string,
        snapshotId: string,
    }
) => {
    return async function (dispatch: Dispatch<any>, state: () => RootState) {
        if (mode === 'view') {
            dispatch(setViewType(EViewType.ViewOnly));
        } else if (mode === 'modify') {
            dispatch(setViewType(EViewType.Modify));
            dispatch(updateIteration());

        } else if (mode === 'create') {
            dispatch(setViewType(EViewType.Modify));
            dispatch(updateCreatingStatus(true));

        } else if (mode === 'reopen and modify') {

            if (!reopenPayload)
                return;

            await SaveAsync(dispatch,
                reopenPayload.dataSource,
                reopenPayload.state,
                {
                    onSaveSetStatusCode: IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value,
                    onSaveStatusScreen: true
                }
            );

            dispatch(setViewType(EViewType.Modify));
            dispatch(updateIteration());

        } else if (mode === 'underwriting review') {

            if (!underwritingPayload)
                return;

            dispatch(setViewType(EViewType.ViewOnly))

            const foundSnapshot = compareUnderWritingReviewToWithoutDispatch(underwritingPayload.state, underwritingPayload.snapshot);

            if (foundSnapshot) {
                // if it has an originating snapshot
                const link = `/underwritingReview/${underwritingPayload.accountId}/${trimGUIDLower(foundSnapshot.bcbs_groupconfigurationsnapshotid!)}/${trimGUIDLower(underwritingPayload.state.data.snapshot.bcbs_groupconfigurationsnapshotid)}`
                underwritingPayload.history.push(link);
            } else {
                // has no originating snapshot, an initial setup case
                const link = `/underwritingReview/${underwritingPayload.accountId}/${underwritingPayload.snapshotId}/`
                underwritingPayload.history.push(link);
            }
        }
    }
}



export const loadSnapshotAction = (dataSource: AppDataSource, accountId: string, xInstructions?: ILoadSnapshotInstructions) => {
    return async function (dispatch: Dispatch<any>, state: () => RootState) {

        console.log("[appl] dataActions.loadSnapshotAction", "instructions", xInstructions)

        const t0 = performance.now();

        const xState = state();
        const newInstructions: ILoadSnapshotInstructions = { ...xInstructions, liveLoad: xState.status.debug.liveload }

        appInsights?.trackTrace({ message: "Custom trace message" }, { width: window.innerWidth, height: window.innerHeight });

        appInsights?.startTrackEvent('loadSnapshotAction');


        // to make sure store is clean prior to load
        if (newInstructions?.purgeStore) {
            dispatch(purgeOnData());
            dispatch(purgeOnStatus());
        }

        dispatch(startSnapshotLoad());

        let context: "primary" | "left" | "right" | "?" = "?";
        let bcbs_groupconfigurationsnapshotid: string | null = null;
        let bcbs_groupconfigurationsnapshotVersionId: string | null = null;

        // TODO until we can parallel load all 3three
        if (newInstructions?.leftSnapshot || newInstructions?.leftSnapshotId) {
            context = "left";
            bcbs_groupconfigurationsnapshotid = newInstructions?.leftSnapshot?.bcbs_groupconfigurationsnapshotid ?? newInstructions?.leftSnapshotId ?? null;
        }
        else if (newInstructions?.rightSnapshot || newInstructions?.rightSnapshotId) {
            context = "right";
            bcbs_groupconfigurationsnapshotid = newInstructions?.rightSnapshot?.bcbs_groupconfigurationsnapshotid ?? newInstructions?.rightSnapshotId ?? null;
        }
        else if (newInstructions?.primarySnapshot || newInstructions?.primarySnapshotId) {
            context = "primary";
            bcbs_groupconfigurationsnapshotid = newInstructions?.primarySnapshot?.bcbs_groupconfigurationsnapshotid ?? newInstructions?.primarySnapshotId ?? null;
        }

        // Snapshot version extraction
        {
            const bcbs_groupconfigurationsnapshotidWithVersionId = bcbs_groupconfigurationsnapshotid?.match(/(.*)-v(.*)/i);
            if (bcbs_groupconfigurationsnapshotidWithVersionId) {

                [, bcbs_groupconfigurationsnapshotid, bcbs_groupconfigurationsnapshotVersionId] = bcbs_groupconfigurationsnapshotidWithVersionId;
                bcbs_groupconfigurationsnapshotVersionId = URIVersionToBlobVersion(bcbs_groupconfigurationsnapshotVersionId)
            }

            if (newInstructions?.primarySnapshotVersionId)
                bcbs_groupconfigurationsnapshotVersionId = newInstructions?.primarySnapshotVersionId;
        }

        try {

            // couple of giant loadings ahead lets check before any load
            // this also allows us to capture session time out for re-auth
            // if we don't have this, the parallel calls below will all trigger
            // re-auth and show like 14 popups :) - that was fun
            await dataSource.crm.whoAmI();

            // TODO this needs to only grab the active ones, else the list is going to grow more and more
            const bcbs_groupconfigurationsnapshots = await dataSource.getGroupConfigurationSnapshotsForAccount(accountId);

            const bcbs_groupconfigurationsnapshot = bcbs_groupconfigurationsnapshotid
                ? bcbs_groupconfigurationsnapshots.find(q => trimGUIDLower(q.bcbs_groupconfigurationsnapshotid!) === trimGUIDLower(bcbs_groupconfigurationsnapshotid!))
                : null;

            // any valid snapshot loaded?
            if (!bcbs_groupconfigurationsnapshot)
                throw new Error("Snapshot not found!");

            let bcbs_config_release: IXrmTypes.bcbs_config_release | null = null;

            // fTODO temporary loading published release for all compare/merge scenarios
            if (context === 'left' || context === 'right')
                bcbs_config_release = await dataSource.getPublishedRelease();
            else
                bcbs_config_release = await dataSource.getRelease(bcbs_groupconfigurationsnapshot._bcbs_releaseid_value!);

            // compare/merge tool?
            if ((window as any).preview)
                if (context === 'left' || context === 'right') {
                    if (newInstructions?.merge) {
                        // for merge left/right we need to load the published release
                        bcbs_config_release = await dataSource.getPublishedRelease();

                    }
                    else {

                        const bcbs_config_release_state = xState?.data?.config.bcbs_config_release;

                        // for compare left/right we need to load the latest of the two config
                        // there are multiple round trips in compare load, which needs to be fixed.
                        if (bcbs_config_release_state) {

                            const bcbs_config_release_snapshot = bcbs_config_release_state.bcbs_config_releaseid === bcbs_groupconfigurationsnapshot._bcbs_releaseid_value
                                ? bcbs_config_release_state
                                : await dataSource.getRelease(bcbs_groupconfigurationsnapshot._bcbs_releaseid_value!);


                            if (bcbs_config_release_state) {
                                const bcbs_config_release_createdon_snapshot = new Date(bcbs_config_release_snapshot?.createdon!);
                                const bcbs_config_release_createdon_state = new Date(bcbs_config_release_state?.createdon!);

                                if (bcbs_config_release_createdon_snapshot.getTime() - bcbs_config_release_createdon_state.getTime() > 1) {
                                    console.log("snapshot release has later date than the store.");
                                    bcbs_config_release = bcbs_config_release_snapshot;
                                } else {
                                    bcbs_config_release = bcbs_config_release_state;
                                }

                            } else {
                                bcbs_config_release = bcbs_config_release_snapshot;
                            }

                        } else {
                            if (!bcbs_config_release) {
                                bcbs_config_release = await dataSource.getRelease(bcbs_groupconfigurationsnapshot._bcbs_releaseid_value!);
                            }
                        }
                    }
                } else {
                    if (!bcbs_config_release) {
                        bcbs_config_release = await dataSource.getRelease(bcbs_groupconfigurationsnapshot._bcbs_releaseid_value!);
                    }
                }

            if (!bcbs_config_release)
                throw new Error("Missing bcbs_config_release");

            const config = await LoadConfigAsync(
                dataSource,
                bcbs_config_release,
                newInstructions,
            );

            // TODO Sanitization Process
            // 1) scan fields for effective date
            // Show if GC effective date is in range (between attribute effective date and expiration date). Else hide and clear.
            // 2) match loaded configuration data elements with what ever group configuration snapshot has

            const bcbs_lob_byValue = objectFlip(IXrmGlobalOptions.bcbs_lob);

            // account information/it must be there
            //TODO: ask alice not to initiate loading if accountid is not there
            //TODO: search source code for my name to find todos that are really for me
            const account = await dataSource.getAccount(accountId);
            const bcbs_agencydetails = await dataSource.getAgencyDetailByGroup(accountId);
            dispatch(updateSnapshotData({ account, bcbs_agencydetails }));

            const bcbs_acctsalesrepcode = account['_bcbs_salesrepid_value'] && await dataSource.getSystemUser(account['_bcbs_salesrepid_value']);

            const bcbs_acctmaintrepcode = bcbs_lob_byValue[account.bcbs_lineofbusiness!] === "National Alliance"
                ? account['_bcbs_accountexecutiveid_value'] && await dataSource.getSystemUser(account['_bcbs_accountexecutiveid_value']) as IXrmTypesExtended.ISystemUserExtended
                : account['_bcbs_servicerepid_value'] && await dataSource.getSystemUser(account['_bcbs_servicerepid_value']) as IXrmTypesExtended.ISystemUserExtended;

            const bcbs_reps = [];
            if (bcbs_acctsalesrepcode)
                bcbs_reps.push(bcbs_acctsalesrepcode);

            if (bcbs_acctmaintrepcode)
                bcbs_reps.push(bcbs_acctmaintrepcode);

            dispatch(updateSnapshotData({ bcbs_reps }));

            dispatch(updateAccount(account));

            // load all snapshots
            dispatch(updateSnapshots(bcbs_groupconfigurationsnapshots));

            let quoteRequests: IXrmTypes.bcbs_quoterequest[] | undefined = undefined;
            let opportunity: IXrmTypes.opportunity | undefined = undefined;

            if (bcbs_groupconfigurationsnapshot?._bcbs_opportunityid_value) {
                opportunity = await dataSource.getOpportunity(bcbs_groupconfigurationsnapshot._bcbs_opportunityid_value);
                if (opportunity) {
                    quoteRequests = await dataSource.getQuoteRequestsForOpportunity(opportunity.opportunityid!);
                }
            }

            dispatch(updateSnapshotData({
                opportunity: opportunity,
                bcbs_quoterequest: quoteRequests
            }));

            dispatch(updateSnapshotData({ snapshot: bcbs_groupconfigurationsnapshot, }));

            let result = await dataSource.downloadGroupConfigurationSnapshot(bcbs_groupconfigurationsnapshot, bcbs_groupconfigurationsnapshotVersionId);

            if (bcbs_groupconfigurationsnapshotVersionId)
                dispatch(updateSnapshotData({ snapshot: bcbs_groupconfigurationsnapshot, }));

            let blank: FAM.IGroupConfigurationData = { ...blankSnapshot() };
            let snapshotStatus: ESnapshotStatus = ESnapshotStatus.untracked;

            // is there a snapshot file?
            if (result) {
                snapshotStatus = ESnapshotStatus.loaded;
                // const fileReq = await fetch_bcbs_groupconfigurationsnapshot_bcbs_snapshot_download(authentication, snapshot.bcbs_groupconfigurationsnapshotid) as string;

                // // Test Code :
                // var x = result.json.benefits[1].nonStandards;
                // for (let index = 0; index < 100; index++) {
                //     const u = structuredClone(x);
                //     u.forEach(e => e.id = uuidv4());
                //     result.json.benefits[1].nonStandards = result.json.benefits[1].nonStandards.concat(u)
                // }

                try {
                    // const data: FAM.IGroupConfigurationData = JSON.parse(fileReq);
                    console.log('[appl] dataActions.loadSnapshotAction', "json", result.json);

                    if (result.json?.iteration !== blank.iteration)
                        console.warn("[appl] dataActions.loadSnapshotAction", "iternation-miss-match");

                }
                catch (exception) {
                    // console.error('parsing json failed for snapshot.', fileReq);
                    throw new Error('snapshot parsing failed');
                }
                finally {

                }
            } else {
                // if there is no iteration on model, means no valid data;
                // lets load blank model
                result = {
                    json: blank,
                    etag: ''
                };

                result.json.id = bcbs_groupconfigurationsnapshot.bcbs_groupconfigurationsnapshotid!;
                snapshotStatus = ESnapshotStatus.untracked;

                if (account.bcbs_lineofbusiness)
                    result.json.lineOfBusiness = {
                        type: 'optionset',
                        value: account.bcbs_lineofbusiness,
                        name: account['bcbs_lineofbusiness@OData.Community.Display.V1.FormattedValue'] ?? ''
                    }

                if (bcbs_groupconfigurationsnapshot.bcbs_name)
                    result.json.name = bcbs_groupconfigurationsnapshot.bcbs_name;

                if (bcbs_groupconfigurationsnapshot.bcbs_effectivedate)
                    result.json.effectiveDate = {
                        type: 'date',
                        value: bcbs_groupconfigurationsnapshot.bcbs_effectivedate
                    }

                result.json.changeType = {
                    type: 'optionset',
                    name: 'Initial Setup',
                    value: IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Initial Setup(808900002)"].value
                }

                console.log('blank snapshot.', bcbs_groupconfigurationsnapshot.bcbs_snapshot_name, result.json);
            }

            dispatch(updateSnapshotData({
                config: config,
            }));

            if (context === "left") {

                // TODO change the type of transformer to full
                /*
                -- remove deactive
                -- match config to a specific config
                -- make sure left and right use the right config for match 
                */
                const loadTransformer = JsonTransformer.getTransformerForEdit(config,
                    dataSource,
                    bcbs_groupconfigurationsnapshot,
                    account,
                    bcbs_config_release,
                    result.json
                )

                await loadTransformer.transformAsync(result.json, bcbs_groupconfigurationsnapshot);

                const leftCompareData: ICompareGroupConfigurationData = {
                    json: result.json,
                    config: config,
                    snapshot: bcbs_groupconfigurationsnapshot,
                    status: defaultStatusSlice()
                };

                // NOTE: we're only setting the status fields which are relevant
                // to the save function
                leftCompareData.status.snapshotBlobVersion = result.etag;
                leftCompareData.snapshot = bcbs_groupconfigurationsnapshot;
                leftCompareData.status.account = account;


                if (newInstructions?.compareTransformReady)
                    dispatch(transformLeftSnapForCompare({ leftSnapshot: leftCompareData, rightSnapshot: state().comp.rightSnap }));

                else
                    dispatch(loadLeftSnap(leftCompareData));

            } else if (context === "right") {

                // TODO change the type of transformer to full
                /*
                -- remove deactive
                -- match config to a specific config
                -- make sure left and right use the right config for match 
                */
                const loadTransformer = JsonTransformer.getTransformerForEdit(
                    config,
                    dataSource,
                    bcbs_groupconfigurationsnapshot,
                    account,
                    bcbs_config_release,
                    result.json
                )

                await loadTransformer.transformAsync(result.json, bcbs_groupconfigurationsnapshot);

                const rightCompareData: ICompareGroupConfigurationData = {
                    json: result.json,
                    config: config,
                    snapshot: bcbs_groupconfigurationsnapshot,
                    status: defaultStatusSlice()
                };

                // NOTE: we're only setting the status fields which are relevant
                // to the save function
                rightCompareData.status.snapshotBlobVersion = result.etag;
                rightCompareData.snapshot = bcbs_groupconfigurationsnapshot;
                rightCompareData.status.account = account;

                dispatch(loadRightSnap(rightCompareData));

                // Merge Snapshot
                // -------------------------------------
                // NOTE: setting some default data points for the merge
                const mergeSnap: ICompareGroupConfigurationData = {
                    config: rightCompareData.config,
                    json: structuredClone(rightCompareData.json),
                    snapshot: structuredClone(rightCompareData.snapshot),
                    status: structuredClone(rightCompareData.status),
                }

                // merge snapshot is always 
                // 1) Non-MNIL
                // 2) Comprehensive Change
                mergeSnap.json.MNIL = { type: 'bool', value: false };
                // removing associated opportunity in case of creating a new one
                mergeSnap.json.opportunity = null;
                let changeType = IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Change(808900004)"];
                mergeSnap.json.changeType = {
                    type: 'optionset',
                    name: changeType.name,
                    value: changeType.value
                }

                dispatch(loadMergeSnap(mergeSnap));

            } else if (context === "primary") {

                const loadTransformer = JsonTransformer.getTransformerForEdit(
                    config,
                    dataSource,
                    bcbs_groupconfigurationsnapshot,
                    account,
                    bcbs_config_release,
                    result.json
                )

                await loadTransformer.transformAsync(result.json, bcbs_groupconfigurationsnapshot);

                // if it has a group collect financial configuration
                if (bcbs_groupconfigurationsnapshot._bcbs_groupcollectfinancialconfigurationid_value && result.json.groupCollectStatus) {

                    // cloned by group collect?
                    if (result.json.groupCollectStatus === EGroupCollectIntegrationStatus.EUntracked) {

                        // emit event should get data ready for save
                        const transform = new JsonTransformer([
                            new AutoCalculateTransformer(config),
                            new RemoveDeactivatedDataTransformer(),
                            new PharmacyResetTransformer(),
                            new GroupCollectEmitEventTransformer(config, bcbs_groupconfigurationsnapshot, EGroupCollectIntegrationStatus.EUntracked),
                        ]);

                        await transform.transformAsync(result.json, bcbs_groupconfigurationsnapshot);
                    }
                    else if (result.json.groupCollectStatus === EGroupCollectIntegrationStatus.EModified) {
                        // updated by group collect?

                        // emit event should get data ready for save
                        const transform = new JsonTransformer([
                            new AutoCalculateTransformer(config),
                            new GroupCollectEmitEventTransformer(config, bcbs_groupconfigurationsnapshot, EGroupCollectIntegrationStatus.EModified),
                        ]);

                        await transform.transformAsync(result.json, bcbs_groupconfigurationsnapshot);
                    } else {
                        throw new Error(`Unexpected group collect status.`);
                    }

                    // forcing ui to ask for a save 
                    dispatch(updateModifiedOn(Date.now()));

                    // app has done its part
                    delete result.json.groupCollectStatus;
                }

                dispatch(updateSnapshotData({
                    json: result.json,
                    snapshot: bcbs_groupconfigurationsnapshot,
                    snapshotVersionId: newInstructions?.primarySnapshotVersionId,
                }));

                dispatch(updateSnapshotBlobVersion(result.etag));

                // todo maybe we can remove this
                dispatch(changeSnapshotStatus(snapshotStatus));

            }

        }
        catch (error: any) {
            alert("An error has occurred. This page cannot load.");

            // lets create a new one is payload is not providing one
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });

            throw error;

        } finally {
            dispatch(endSnapshotLoad());
            appInsights?.stopTrackEvent('loadSnapshotAction');
            console.log(`[appl] dataActions.loadSnapshotAction executiontime ${performance.now() - t0}ms`);
        }


    }
}

export const loadCheckListAction = (dataSource: AppDataSource, onComplete?: (checkList: ICheckList[]) => void) => {
    return async function (dispatch: Dispatch<any>, state: () => RootState) {

        try {

            const xState = state();

            if (!xState?.data?.snapshot?.bcbs_groupconfigurationsnapshotid)
                throw new Error("Associated snapshot was not loaded.");


            let bcbs_emchecklists = (await dataSource.getChecklistsBySnapshotId(xState.data.snapshot.bcbs_groupconfigurationsnapshotid!))?.value ?? [];

            const checklistsToAdd: IXrmTypes.bcbs_emchecklist[] = [];
            xState.data.json.plans.forEach(eachPlan => {

                // ignore processing deacitvated plans
                if (eachPlan.deactivated)
                    return;

                // billing plans dont get a check list
                if (eachPlan.billingOnlyPlan)
                    return;

                // ignore if it has already been created for a given plan
                if (bcbs_emchecklists.find(q => q.bcbs_planid === eachPlan.id))
                    return;

                const t: IXrmTypes.bcbs_emchecklist = {
                    bcbs_name: eachPlan.name,
                    bcbs_planid: eachPlan.id,
                    _bcbs_groupconfigurationsnapshotid_value: xState.data.snapshot.bcbs_groupconfigurationsnapshotid,
                    bcbs_snapshotversion: xState.data.snapshot.bcbs_blobversion,
                } as IXrmTypes.bcbs_emchecklist;

                t.bcbs_filename = dataSource.generateEMChecklistFilename(xState.data.snapshot, t);

                checklistsToAdd.push(t);
            });

            const newCheckListsPromises = checklistsToAdd.length > 0
                ? checklistsToAdd.map(t => dataSource.create_bcbs_emchecklist(t))
                : [];

            if (newCheckListsPromises.length > 0)
                await Promise.all(newCheckListsPromises);

            // anything got created? reload
            if (checklistsToAdd.length > 0)
                bcbs_emchecklists = (await dataSource.getChecklistsBySnapshotId(xState.data.snapshot.bcbs_groupconfigurationsnapshotid!))?.value ?? [];

            const checkList: ICheckList[] = bcbs_emchecklists.map(bcbs_emchecklist => {
                return {
                    bcbs_emchecklist: bcbs_emchecklist,
                    blob: null as any,
                    blobEtag: null,
                    versionNumber: bcbs_emchecklist.versionnumber,
                }
            });

            dispatch(updateSnapshotData({
                checkList: checkList
            }));

            onComplete?.(checkList);

        } catch (error: any) {
            // lets create a new one is payload is not providing one
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });

            console.error("loadCheckListAction, see details.", error?.toString());

            throw error;

        } finally {

        }

    }
}


export const LoadSnapshotWithVersionAction = (dataSource: AppDataSource, history: any, versionId: string) => {
    return async function (dispatch: Dispatch<any>, state: () => RootState) {
        const xState = state();

        dispatch(setViewType(EViewType.ViewOnly));
        dispatch(updateDebug({ autoSaveOff: true }));
        dispatch(loadSnapshotAction(dataSource,
            xState.data.account?.accountid!,
            {
                primarySnapshot: xState.data.snapshot,
                primarySnapshotVersionId: versionId,
                merge: false,
            })
        );
        history.goBack();
    }
}

//export const loadComparisonSnapshots = (ds: AppDataSource, id: string, snapshot: any, side: string) => {
//    return function (dispatch: any) {
//        (async () => {

//            if (side === "left")
//                dispatch(loadSnapshot(ds, id, {
//                    leftSnapshot: snapshot,
//                    leftSnapshotId: snapshot
//                }));

//            if (side === "right")
//                dispatch(loadSnapshot(ds, id, {
//                    rightSnapshot: snapshot,
//                    rightSnapshotId: snapshot
//                }));
//        })()
//    }
//}

export const autoGenerateFeesAndPremiumsFeesAction = () => {
    return function (dispatch: Dispatch<any>, state: () => RootState) {
        //TODO: MIKE
        // const newFeesAndPremiums = [...data.feesAndPremiums];

        const cState = state();
        const newFeeAndPremiums = structuredClone(cState.data.json.feesAndPremiums);

        const data = cState.data.json;
        const config = cState.data.config;

        if (cState.status.debug)
            debugger;

        //***TODO*** I don't see where a Fee and Premium/Admin Fee is related to a Plan 
        //Need to be able to set the Agent Number on the GC, the Med Short Name on the Medical and the Medical Benefit Type on the Medical
        //That will let BRK, CPF, HC1 and PCM Generate

        //Grab all BCC Admins where the Master Attribute has a value (these are automatically created ones) AND
        //the Funding Types (of the Plan) match
        //configX.bcbs_bccadmin.filter(q=>q.bcbs_attributevalue)
        //configX.masterattributes.filter(q => q.bcbs_config_masterattributeid)
        //let x = configX.masterattributes.find(q => q.bcbs_name === "Medical Benefit Type");
        //x.value
        //const att = data.benefits[0].fields.find(q => q.configRefId === '8888cb95-ce4b-ec11-8f8e-000d3a3349fe')!;
        const snapshotFundingType = data.fundingType?.name;
        let finalFundingType = '';
        if (!snapshotFundingType)
            finalFundingType = IXrmGlobalOptions.bcbs_fundingtypes.AdministrativeServicesOnly.value.toString();
        if (snapshotFundingType === 'Administrative Services Only')
            finalFundingType = IXrmGlobalOptions.bcbs_fundingtypes.AdministrativeServicesOnly.value.toString();
        if (snapshotFundingType === 'ASO with Stop Loss')
            finalFundingType = IXrmGlobalOptions.bcbs_fundingtypes.ASOwithStopLoss.value.toString();
        if (snapshotFundingType === 'Fully Insured')
            finalFundingType = IXrmGlobalOptions.bcbs_fundingtypes.FullyInsured.value.toString();
        if (snapshotFundingType === 'National Alliance')
            finalFundingType = IXrmGlobalOptions.bcbs_fundingtypes.NationalAlliance.value.toString();
        if (snapshotFundingType === 'Level Funded')
            finalFundingType = IXrmGlobalOptions.bcbs_fundingtypes.LevelFunded.value.toString();
        if (snapshotFundingType === 'Mixed')
            finalFundingType = IXrmGlobalOptions.bcbs_fundingtypes.AdministrativeServicesOnly.value.toString();

        const BCCAdminsFull = config.bcbs_bccadmin.filter(q => q._bcbs_build_masterattributeid_value);
        //const BCCAdminsFinal = BCCAdminsFull.filter(q => q.bcbs_fundingtypescode?.includes(bcbs_fundingtypes.AdministrativeServicesOnly.value.toString()));//Hardcoded ASO - needs to pull funding type of Plan 
        const BCCAdminsFinal = BCCAdminsFull.filter(q => q.bcbs_fundingtypescode?.includes(finalFundingType));//Hardcoded ASO - needs to pull funding type of Plan 

        //For each Fee and Premium, grab the Attributes associated with the first Plan associated to the Fee and Premium where
        //the Master Attribute is in the list of auto generated ones
        //*I really want to start from the Fees and Premiums to see what Plan(s) are associated can I do that? *
        //const newPlans = data.plans.map(plan => {
        //return {
        //...plan,
        //FeeAndPremiums: data.feesAndPremiums.find(f => f.id === plan.FeeAndPremiumsRef),
        //benefits: data.benefits.filter(t => plan.benefitRefs.some(q => t.id === q.id))
        //}
        //});
        // newPlans[0].benefits[1].bcbs_name //category name

        //const feeAndPremiumsWihFirstPlan = data.feesAndPremiums.map(fnp => {
        //return { ...fnp, firstMatchingPlan: data.plans.find(p => p.FeeAndPremiumsRef = fnp.id) }
        //})


        // data.plans.find(plan => plan.FeeAndPremiumsRef === )
        //data.plans.forEach(plan => {

        //})

        //Compare the requred value on the BCC Admin to the Attributes value... if they match OR if the BCC Admin is null create a Fee array to store
        //These as potential fees to create
        //I need a way to see the type (Category) of Benefits is the only way through CategoryID's?
        //const testPlan1BenefitID = data.plans[1].benefitRefs[2].id;
        //const Plan1MedicalBenefit = benefits.filter(q => q.id === testPlan1BenefitID);
        //debugger;

        //const BCCADminIDs = [];

        //Plan1MedicalBenefit[0].fields.forEach(planField => {
        //BCCAdminsFinal.forEach(BCCAdmin => {
        //if (BCCAdmin._bcbs_build_masterattributeid_value === planField.masterAttributeID) {

        //const primaryValue = planField.values[0].value as FAM.IPrimaryAttributeValue
        //if (BCCAdmin.bcbs_attributevalue === null) {
        //BCCADminIDs.push(BCCAdmin);
        //}
        //else if (BCCAdmin.bcbs_attributevalue?.includes(primaryValue.primary))//Why can't I access Primary? Why does it seem the Primary value holds an option set id (how do I get the Option Set value?)
        //{
        //BCCADminIDs.push(BCCAdmin);
        //}
        //}

        //})

        //})


        //Finally check to see if a fee with that BCC Admin already exists for the Fee and Premium... if it does we don't need to create
        //it again
        //newFeesAndPremiums.find

        //How do I simply hardcode a fee and create it? I'm assuming I have to build a Fee, set its values and then save it?
        // const feeAndPremiumCategoryId = '7c8c262c-d778-ec11-8d21-0022480b0e40'
        const feeNPTabId = 'f5625f99-d778-ec11-8d21-000d3a35957e'

        //This is the Attribute GUID of the Attribute whose name is Fee
        // see (https://bcbsonlinedevgl.crm.dynamics.com/main.aspx?appid=975825e9-352c-ec11-b6e5-000d3a5b93e5&forceUCI=1&newWindow=true&pagetype=entityrecord&etn=bcbs_config_attribute&id=50d7c5a7-d590-ec11-b400-0022480a93ed&formid=3b6991fc-8a92-40f3-a9d4-1c178728cc9d)
        const feeAttributesetAttributeId = 'f5646443-d590-ec11-b400-0022480a93ed';

        //const feeAttributesetAttribute = cState.data.config.cache.map_bcbs_config_attribute[feeAttributesetAttributeId];
        const feeattributesetMasterAttribute = cState.data.config.cache.map_bcbs_config_masterattribute[feeAttributesetAttributeId];



        newFeeAndPremiums.forEach(eachFeeAndPremium => {

            const firstPlan = data.plans.find(plan => plan.feesAndPremiumsRef && plan.feesAndPremiumsRef.id === eachFeeAndPremium.id);
            const firstPlanBenefits = data.benefits.filter(b => firstPlan?.benefitRefs.some(q => q.id === b.id));
            const medBenefit = firstPlanBenefits.filter(q => q.bcbs_name?.includes("Medical"));
            const rxBenefit = firstPlanBenefits.filter(q => q.bcbs_name?.includes("Pharmacy"));
            const ancBenefit = firstPlanBenefits.filter(q => q.bcbs_name?.includes("Ancillary"));
            const visBenefit = firstPlanBenefits.filter(q => q.bcbs_name?.includes("Vision"));
            const denBenefit = firstPlanBenefits.filter(q => q.bcbs_name?.includes("Dental"));
            //Need to ensure the Fee and Premium doesn't have a Fee of this BCC Admin before creating
            //const currentFees = eachFeeAndPremium.fees
            const bccAdmins = BCCAdminsFinal.filter(eachBccAdmin =>
                firstPlanBenefits.some(eachBenefit => {
                    return eachBenefit.fields.some(eachField => {
                        //console.log("CompareLog", eachBccAdmin, eachField);
                        const fees = eachFeeAndPremium.attributeSets
                            .filter(t1 => t1.attributeSetRef.id === feeAttributesetAttributeId) //pick all fees
                        const feeBCCAdmins = fees.map(t2 => { return t2.fields.filter(w => w.name === "BCC Admin") }).flat(1)
                        const alreadyExists = feeBCCAdmins.some(q => q.values[0]?.value?.type === "entity" && q.values[0]?.value?.name === eachBccAdmin.bcbs_name)

                        if (eachBccAdmin.bcbs_name === "BRK | AGENT FEE (Commission) | Medical" && medBenefit.length > 0 && !alreadyExists)
                            return true;
                        else if (eachBccAdmin.bcbs_name === "HC1 | HCR Reinsurance Fee | Medical" && medBenefit.length > 0 && !alreadyExists)
                            return true;
                        else if (eachBccAdmin.bcbs_name === "PPF | Partner Plan Fee | Medical" && medBenefit.length > 0 && !alreadyExists)
                            return true;
                        else if (eachBccAdmin.bcbs_name === "CMM | COMPREHENSIVE | Medical" && medBenefit.length > 0 && !alreadyExists) {
                            if (medBenefit[0]?.tierSetRef?.name === "Comprehenseive")
                                return true;
                        }
                        else if (eachBccAdmin.bcbs_name === "CPF | Control Plan Fee | Medical" && medBenefit.length > 0 && !alreadyExists) {
                            if (medBenefit[0]?.tierSetRef?.name === "Comprehenseive")
                                return true;
                        }
                        else if (eachBccAdmin.bcbs_name === "CPF | PREFERRED COMP MOD (PPO/EPO) ASO | Medical" && medBenefit.length > 0 && !alreadyExists) {
                            if (medBenefit[0]?.tierSetRef?.name !== "Comprehenseive")
                                return true;
                        }
                        else if (eachBccAdmin.bcbs_name === "PCM | PREFERRED COMP MOD (PPO/EPO) | Medical" && medBenefit.length > 0 && !alreadyExists) {
                            if (medBenefit[0]?.tierSetRef?.name !== "Comprehenseive")
                                return true;
                        }


                        else if (eachBccAdmin.bcbs_name === "CPF | DENTAL | Dental" && denBenefit.length > 0 && !alreadyExists)
                            return true;
                        else if (eachBccAdmin.bcbs_name === "CPF | Vision | Vision" && visBenefit.length > 0 && !alreadyExists)
                            return true;
                        else if (eachBccAdmin.bcbs_name === "VIS | Vision | Medical" && visBenefit.length > 0 && !alreadyExists)
                            return true;
                        else if (eachBccAdmin.bcbs_name === "EES | Engagement Suite Standard Package | Medical" && ancBenefit.length > 0 && !alreadyExists)
                            return true;
                        else if (eachBccAdmin.bcbs_name === "HCC | Health Coaching - Chronic Condition | Medical" && ancBenefit.length > 0 && !alreadyExists)
                            return true;
                        else if (eachBccAdmin.bcbs_name === "HCL | Health Coaching - Lifestyle | Medical" && ancBenefit.length > 0 && !alreadyExists)
                            return true;


                        if (eachBccAdmin.bcbs_attributevalue === null && eachField.masterAttributeID === eachBccAdmin._bcbs_build_masterattributeid_value && !alreadyExists)
                            return true;




                        if (eachField.masterAttributeID === eachBccAdmin._bcbs_build_masterattributeid_value && !alreadyExists) {
                            if (eachField && eachField.values && eachField.values[0].value) {
                                const eachFirstFieldValue = eachField.values[0].value.type === "string"
                                    ? eachField.values[0].value.value
                                    : eachField.values[0].value.type === "entity"
                                        ? eachField.values[0].value.name
                                        : eachField.values[0].value.type === "bool"
                                            ? (eachField.values[0].value.value === true ? "Yes" : "No")
                                            : null

                                if (eachFirstFieldValue && eachBccAdmin.bcbs_attributevalue?.includes(eachFirstFieldValue))
                                    return true;
                            }
                        }
                        return false;

                    })
                }))

            //eachFeeAndPremium.attributeSets = [];
            bccAdmins.forEach(bccAdmin => {//Loops through each BCC Admin that needs to have a Fee created for this Fee and Premium
                let feeAttributeSet = createAttributeSet(cState.data, feeattributesetMasterAttribute);
                feeAttributeSet.name = "Fee";

                feeAttributeSet.fields.forEach(eachField => {
                    let value: (FAM.TValue | null) = null;

                    const att = config.cache.map_bcbs_config_attribute[eachField.configRef.id];
                    //debugger;
                    switch (att.bcbs_config_attributeid) {

                        // link to crm 
                        // BCC Admin
                        case '20eab0ef-e778-ec11-8d21-000d3a35957e':
                            const t: FAM.IEntityReferenceValue = {
                                type: 'entity',
                                entityType: 'bcbs_bccadmin',
                                id: bccAdmin.bcbs_bccadminid!,
                                name: bccAdmin.bcbs_name!
                            }
                            value = t;
                            break;

                        // link to crm 
                        // Fee Type
                        case '1ceab0ef-e778-ec11-8d21-000d3a35957e':
                            const x: FAM.IEntityReferenceValue = {
                                type: 'entity',
                                entityType: 'bcbs_config_optionsetitem',
                                id: 'd34bfc88-d678-ec11-8d21-000d3a35957e',
                                name: "Per Employee Per Month (PEPM)"
                            }
                            value = x;
                            break;

                        // link to crm 
                        // Bill Group
                        case '28eab0ef-e778-ec11-8d21-000d3a35957e':
                            const y: FAM.IBooleanValue = {
                                type: 'bool', value: true
                            }
                            value = y;
                            break;

                    }


                    //if (att.bcbs_name?.startsWith('BCC Admin')) {
                    //    const t: FAM.IEntityReferenceValue = {
                    //        type: 'entity',
                    //        entityType: 'bcbs_bccadmin',
                    //        id: bccAdmin.bcbs_bccadminid!,
                    //        name: bccAdmin.bcbs_name!
                    //    }
                    //    value = t;
                    //}

                    //if (att.bcbs_name?.startsWith('Fee Type')) {
                    //    const t: FAM.IOptionSetValue = {
                    //        type: 'optionset',
                    //        value: 1,
                    //        name: "Per Employee Per Month (PEPM)"
                    //    }
                    //    value = t;
                    //}

                    //if (att.bcbs_name?.startsWith('Bill Group?')) {
                    //    const t: FAM.IBooleanValue = {
                    //        type: 'bool', value: true
                    //    }
                    //    value = t;
                    //}

                    //const fee1DefaulTierValue: FAM.IFlexibleFieldValue = {
                    //    id: uuidv4(),
                    //    type: 'primary',
                    //    tierRef: null,
                    //    value: value
                    //}
                    eachField.values[0].value = value;
                });
                //let temp = feeAttributeSet;
                eachFeeAndPremium.attributeSets.push(feeAttributeSet);

                console.log("Fee And Premium Calculated :", eachFeeAndPremium);
            })
        })

        //configX.bcbs_bccadmin.forEach(q => {q.})

        //newFeesAndPremiums.forEach(q => {q.fees.push})

        // const adminFee = config.
        // const PLACE HOLDER FOR Michael
        // const configuration =
        dispatch(updateFeesAndPremiums(newFeeAndPremiums));
    }
}

// unmounts and remounts the root after a delay
// see context::state.status.recycling
export const WithRecycle = (action: () => void, ms: number) => {
    return async function (dispatch: Dispatch<any>, state: () => RootState) {
        dispatch(setStatusRecycle(true));
        setTimeout(() => {
            action?.()
            dispatch(setStatusRecycle(false));
        }, ms);
    }
}


export const UpdateGroupConfigurationProperties = (updateValue: IGroupConfigurationUpdate) => {
    return async function (dispatch: Dispatch<any>, state: () => RootState) {
        dispatch(addUpdateGroupConfiguration(updateValue));

    }
}

export const loadOpportunityProductAction = (
    dataSource: AppDataSource,
    bcbs_opportunityproductid: string,
    instructions?: {
        loadscreen?: 'none' | 'full' | 'partial',
    }
) => {
    return async function (dispatch: Dispatch<any>, stateSelector: () => RootState) {
        try {

            let state = stateSelector();

            if (!instructions?.loadscreen || instructions?.loadscreen === 'full')
                dispatch(startSnapshotLoad());

            // do we reload the config
            if (!state.data.config.bcbs_config_release) {
                const config = await LoadConfigAsync(dataSource, undefined);
                dispatch(updateSnapshotData({
                    config: config,
                }));
            }

            state = stateSelector();

            const response = await dataSource.getOpportunityProductAndOptionsAndBenefits(bcbs_opportunityproductid)

            const opportunityProduct: FAM.IOpportunityProduct = {
                opportunity: response.opportunity,
                bcbs_opportunityproduct: response.bcbs_opportunityproduct,
                bcbs_quoterequest: response.bcbs_quoterequest,
                options: []
            }

            response.bcbs_opportunitybenefit.forEach(each_bcbs_opportunitybenefit => {

                const option = createOpportunityOptionAndBenefits(
                    state.data.config,
                    each_bcbs_opportunitybenefit.bcbs_quoterequest,
                    response.bcbs_opportunityproduct,
                    // From CRM:
                    {
                        bcbs_opportunitybenefits: each_bcbs_opportunitybenefit.bcbs_opportunitybenefits,
                        bcbs_opportunityoption: each_bcbs_opportunitybenefit.bcbs_opportunityoption,
                    }
                );

                option.bcbs_opportunitybenefits = each_bcbs_opportunitybenefit.bcbs_opportunitybenefits;

                opportunityProduct.options.push(option)
            })

            var account = await dataSource.getAccount(response.bcbs_opportunityproduct._bcbs_groupid_value!);

            dispatch(updateSnapshotData({
                opportunityProduct: opportunityProduct,
                account: account,

                // to keep compaitble with snapshot save
                opportunity: opportunityProduct.opportunity,
            }));
        }
        catch (error: any) {
            alert("An error has occurred. This page cannot load.");

            // lets create a new one is payload is not providing one
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });

            throw error;

        } finally {
            dispatch(endSnapshotLoad());
            appInsights?.stopTrackEvent('loadSnapshotAction',);
        }
    }
}

export const saveOpportunityProductDataAction = (dataSource: AppDataSource) => {
    return async function (dispatch: Dispatch<any>, state: () => RootState) {
        try {
            let stateInstance = state();

            if (stateInstance.status.snapshotLoading) {
                console.log("Snapshot processing, skipping save");
                return;
            }

            console.log("stateInstance", stateInstance);

            const convert_iflexiblefieldvalue_to_bcbs_opportunitybenefit = (
                field: FAM.IFlexibleField,
                value: FAM.IFlexibleFieldValue,
                tierLevels: Record<string, FAM.ITierLevel>
            ) => {
                const configAttribiute = config.cache.map_bcbs_config_attribute[field.configRef.id];
                const configMasterAttribiute = config.cache.map_bcbs_config_masterattribute[configAttribiute._bcbs_config_masterattributeid_value!];
                const controlType = configMasterAttribiute.bcbs_attributetypecode as EControlType;

                const benefit: Partial<IXrmTypes.bcbs_opportunitybenefit> = {};
                // benefit.bcbs_opportunitybenefitid = field.id

                benefit._bcbs_config_masterattributeid_value = configMasterAttribiute.bcbs_config_masterattributeid;
                benefit._bcbs_config_attributeid_value = configAttribiute.bcbs_config_attributeid;

                benefit.bcbs_name = calcFieldName(field, value, configAttribiute, tierLevels);
                benefit.bcbs_opportunitybenefitid = value.id;

                if (!field.visibility && value && value.value) {
                    benefit.bcbs_value = JSON.stringify(value);
                    benefit.bcbs_friendlyvalue = friendlyValue(controlType, value.value);
                }
                else {
                    benefit.bcbs_value = null as any;
                    benefit.bcbs_friendlyvalue = null as any;
                }

                return benefit;

            }

            // Changeset
            const createChangeSet = <T extends unknown>(
                origin: T[],
                target: T[],
                {
                    targetKeyPredicate,
                    originKeyPredicate
                }:
                    {
                        targetKeyPredicate: (t: T) => string,
                        originKeyPredicate: (t: T) => string
                    }
            ) => {

                type MergeRecord<T> = {
                    id: string,
                    index: number,
                    onOrigin: T | null,
                    onTarget: T | null
                };

                type MergeRecordLookup<T> = Record<string, MergeRecord<T>>;

                const merged: MergeRecordLookup<T> = {};

                target.forEach((item, index) => {
                    const matchId = targetKeyPredicate(item);

                    // fetch the merge record, if doesnt exist create one
                    const merge = merged[matchId] ?? {
                        id: matchId,
                        index: index,
                        onOrigin: null,
                        onTarget: null,
                    };

                    merged[matchId] = merge;

                    merge.onTarget = item;
                });

                origin.forEach((item, index) => {
                    const matchId = originKeyPredicate(item);

                    // fetch the merge record, if doesnt exist create one
                    const merge = merged[matchId] ?? {
                        id: matchId,
                        index: index,
                        onOrigin: null,
                        onTarget: null,
                    };
                    merged[matchId] = merge;

                    merge.onOrigin = item;
                });

                return merged;
            }

            const convert_iopportunityproductoptionbenefit_to_bcbs_opportunityoption = (option: FAM.IOpportunityProductOptionBenefit) => {
                const bcbs_opportunityoption: IXrmTypes.bcbs_opportunityoption = {
                    bcbs_opportunityoptionid: option.id!,
                    bcbs_name: option.name,
                    bcbs_buyupuwsinglei: option.bcbs_buyupuwsinglei,
                    bcbs_buyupuwfamilyi: option.bcbs_buyupuwfamilyi,
                    bcbs_buyupuweechildreni: option.bcbs_buyupuweechildreni,
                    bcbs_buyupuweespousei: option.bcbs_buyupuweespousei,
                    bcbs_buyupuwtotali: option.bcbs_buyupuwtotali,
                    bcbs_buyupuwsinglem: option.bcbs_buyupuwsinglem,
                    bcbs_buyupuwfamilym: option.bcbs_buyupuwfamilym,
                    bcbs_buyupuweechildrenm: option.bcbs_buyupuweechildrenm,
                    bcbs_buyupuweespousem: option.bcbs_buyupuweespousem,
                    bcbs_buyupuwtotalm: option.bcbs_buyupuwtotalm,
                    bcbs_annualpremium: option.bcbs_annualpremium,
                };

                bcbs_opportunityoption._owningteam_value = option.bcbs_opportunityproduct?._owningteam_value;

                // tiers
                bcbs_opportunityoption.bcbs_tierdefinition = JSON.stringify(option.bcbs_tierdefinition);

                // Status/State fields
                if (option.deactivated) {
                    bcbs_opportunityoption.statuscode = IXrmOptions.bcbs_opportunityoption.statuscode['Inactive(2)'].value;
                    bcbs_opportunityoption.statecode = IXrmOptions.bcbs_opportunityoption.statecode['Inactive(1)'].value;
                }
                else {
                    if (option.won) {
                        bcbs_opportunityoption.statuscode = IXrmOptions.bcbs_opportunityoption.statuscode['Won(808900001)'].value;
                    } else {
                        bcbs_opportunityoption.statuscode = IXrmOptions.bcbs_opportunityoption.statuscode['Active(1)'].value;
                    }
                    bcbs_opportunityoption.statecode = IXrmOptions.bcbs_opportunityoption.statecode['Active(0)'].value;
                }

                return bcbs_opportunityoption;
            };

            function extractOnlyChangedFields<T extends Record<string | number, any>>(
                origin: T,
                target: T,
                fields: string[] | null,
                includeNull: boolean = true
            ) {
                const changedFields = {} as Partial<T>;

                // if no field specified, create a unique key set between source and target
                fields = fields ?? [...new Set([...Object.keys(origin), ...Object.keys(target)])];;

                fields.forEach(key => {
                    // changed?
                    if (origin[key] !== target[key])
                        if (target[key] !== null) {
                            changedFields[key as any] = target[key];
                        }
                        else {
                            if (includeNull)
                                changedFields[key as any] = target[key];
                        }
                });
                return changedFields;
            }

            dispatch(setProcessing(true));

            const config = stateInstance.data.config;

            const stateOptions = stateInstance.data.opportunityProduct?.options;

            const optionPromises: any[] = [];

            // Option:
            // Add/Update/Delete
            // ----------------------------------------------------------------
            stateOptions?.forEach(stateOption => {

                // Dont save if requested
                if (stateOption.instructions.onSaveOptionSkip) {
                    console.log("Skipping save for option", stateOption);
                    return;
                }

                const bcbs_opportunityoption_mapped = convert_iopportunityproductoptionbenefit_to_bcbs_opportunityoption(stateOption);

                // relate option to product 
                bcbs_opportunityoption_mapped._bcbs_opportunityproductid_value = stateInstance.data.opportunityProduct?.bcbs_opportunityproduct.bcbs_opportunityproductid!;

                // CREATE 
                if (!stateOption.bcbs_opportunityoption) {
                    const promise = dataSource.save_bcbs_opportunityoption(bcbs_opportunityoption_mapped);
                    optionPromises.push(promise);
                }

                // UPDATE
                if (stateOption.bcbs_opportunityoption) {

                    // fields have changed?
                    const observableFields: (keyof IXrmTypes.bcbs_opportunityoption)[] = [
                        'bcbs_name',
                        'bcbs_buyupuwsinglei',
                        'bcbs_buyupuwfamilyi',
                        'bcbs_buyupuweechildreni',
                        'bcbs_buyupuweespousei',
                        'bcbs_buyupuwtotali',
                        'bcbs_buyupuwsinglem',
                        'bcbs_buyupuwfamilym',
                        'bcbs_buyupuweechildrenm',
                        'bcbs_buyupuweespousem',
                        'bcbs_buyupuwtotalm',
                        'bcbs_annualpremium',
                        'statecode',
                        'statuscode',
                        'bcbs_tierdefinition',
                    ];

                    // any of observerd fields changed?
                    const updatedFields = observableFields.filter(key => {
                        if (stateOption.bcbs_opportunityoption![key] !== bcbs_opportunityoption_mapped[key])
                            return true;
                    });

                    // anything updated?
                    if (updatedFields.length > 0) {

                        // only grab the changed fields
                        const bcbs_opportunitybenefit_changed = {} as IXrmTypes.bcbs_opportunityoption;
                        updatedFields.forEach(key => {
                            bcbs_opportunitybenefit_changed[key] = bcbs_opportunityoption_mapped[key] as any;
                        });

                        const promise = dataSource.save_bcbs_opportunityoption(bcbs_opportunitybenefit_changed, stateOption.bcbs_opportunityoption.bcbs_opportunityoptionid!);
                        optionPromises.push(promise);
                    }
                }

            });

            // wait for all options to be saved
            await Promise.all(optionPromises);

            const optionBenefitPromises: any[] = [];

            // Benefits:
            // Add/Update/Delete of benefits for each option
            // ----------------------------------------------------------------
            stateOptions?.forEach(stateOption => {

                if (stateOption.instructions.onSaveOptionBenefitsSkip) {
                    console.log("Skipping save for option benefits", stateOption);
                    return;
                }

                const bcbs_opportunitybenefits_mapped: IXrmTypes.bcbs_opportunitybenefit[] = []

                const bcbs_opportunitybenefits_origin = stateOption.bcbs_opportunitybenefits ?? [];

                const tierLevels = ArrayToLookup(stateOption.bcbs_tierdefinition.tierLevels);

                // converting to benefits
                stateOption.fields.forEach(eachField => {
                    eachField.values.forEach(eachFieldValue => {
                        const benefit = convert_iflexiblefieldvalue_to_bcbs_opportunitybenefit(eachField, eachFieldValue, tierLevels);

                        // relate to option
                        benefit._bcbs_opportunityoptionid_value = stateOption.id;

                        // relate to product
                        benefit._bcbs_opportunityproduct_value = stateOption.bcbs_opportunityproduct?.bcbs_opportunityproductid!;

                        benefit._owningteam_value = stateOption.bcbs_opportunityproduct?._owningteam_value;

                        bcbs_opportunitybenefits_mapped.push(benefit);
                    });
                });

                // merging
                const mergedBenefitsLookup = createChangeSet<IXrmTypes.bcbs_opportunitybenefit>(
                    bcbs_opportunitybenefits_origin,
                    bcbs_opportunitybenefits_mapped,
                    {
                        originKeyPredicate: (item) => {
                            return item.bcbs_opportunitybenefitid!;
                            // let key = item._bcbs_config_attributeid_value!;

                            // var bcbs_value = JSON.parse(item.bcbs_value ?? '{}') as FAM.IFlexibleFieldValue;

                            // if (bcbs_value.type)
                            //     key = key + "-" + bcbs_value.type;

                            // if (bcbs_value.tierRef?.id)
                            //     key = key + "-" + bcbs_value.tierRef.id;

                            // return key;
                        },

                        targetKeyPredicate: (item) => {
                            return item.bcbs_opportunitybenefitid!;

                            // let key = item._bcbs_config_attributeid_value!;

                            // var bcbs_value = JSON.parse(item.bcbs_value ?? '{}') as FAM.IFlexibleFieldValue;

                            // if (bcbs_value.type)
                            //     key = key + "-" + bcbs_value.type;

                            // if (bcbs_value.tierRef?.id)
                            //     key = key + "-" + bcbs_value.tierRef.id;

                            // return key;
                        },
                    }
                );

                // judgements
                const mergedBenefits = Object.values(mergedBenefitsLookup).sort((a, b) => a.index - b.index);

                const result: { action: 'untracked' | 'modified' | 'delete' | '', change: IXrmTypes.bcbs_opportunitybenefit }[] =
                    mergedBenefits.map(t => {

                        // CREATE, we need to create a new full record
                        if (t.onTarget && !t.onOrigin) {
                            // * ignore blank/null/undefined values for create
                            if (t.onTarget.bcbs_value)
                                return {
                                    action: 'untracked',
                                    change: t.onTarget
                                }
                        }

                        // UPDATE
                        // TODO implementing field change detection and only updating fields if they have changed.
                        // * blank values should remove
                        // * (TODO) Fields should be updated and identified if they are different
                        if (t.onTarget && t.onOrigin) {
                            if (t.onTarget.bcbs_value !== t.onOrigin.bcbs_value) {
                                return {
                                    action: 'modified',
                                    change: t.onTarget
                                }
                            }
                        }

                        // DELETE
                        // * blank values should remove
                        if (!t.onTarget && t.onOrigin) {
                            return {
                                action: 'delete',
                                change: t.onOrigin
                            }
                        }

                        // default case
                        return {
                            action: '',
                            change: {} as IXrmTypes.bcbs_opportunitybenefit
                        };
                    });

                result.forEach(({ action, change }) => {
                    if (action === 'untracked') {
                        const promise = dataSource.save_bcbs_opportunitybenefits(change);
                        optionBenefitPromises.push(promise);
                    }

                    if (action === 'modified') {
                        const promise = dataSource.save_bcbs_opportunitybenefits(change, change.bcbs_opportunitybenefitid);
                        optionBenefitPromises.push(promise);
                    }
                })

            })

            await Promise.all(optionBenefitPromises);

            dispatch(
                loadOpportunityProductAction(
                    dataSource,
                    stateInstance.data.opportunityProduct?.bcbs_opportunityproduct.bcbs_opportunityproductid!,
                    {
                        loadscreen: 'none'
                    }
                )
            );
        }

        catch (error: any) {
            alert("An error has occurred. This page cannot load.");

            // lets create a new one is payload is not providing one
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });

            throw error;

        } finally {
            dispatch(setProcessing(false));
            // dispatch(endSnapshotLoad());
            appInsights?.stopTrackEvent('loadSnapshotAction',);
        }


    }
}

export const UpdateOpportunityProductAction = (
    dataSource: AppDataSource,
) => {
    return async function (dispatch: Dispatch<any>, stateSelector: () => RootState) {

        try {
            dispatch(startSnapshotLoad());

            const xState = stateSelector();

            const config = xState.data.config!;
            let opportunityProductRef = { ...xState.data.opportunityProduct! };
            opportunityProductRef.options = [...opportunityProductRef.options];

            const importModule = await import("global/Transform/Sprint28Migration")
            const { QuoteRequestTransformer } = importModule;
            const result = await QuoteRequestTransformer(config, opportunityProductRef);

            dispatch(
                updateSnapshotData({
                    opportunityProduct: result,
                })
            );
        }
        catch (error: any) {
            // lets create a new one is payload is not providing one
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });

            throw error;

        } finally {
            dispatch(endSnapshotLoad());
            appInsights?.stopTrackEvent('loadSnapshotAction',);
        }


    }
}


export const SaveQuoteRequestAsGroupConfigurationAction = (dataSource: AppDataSource, options: FAM.IOpportunityProductOptionBenefit[]) => {
    return async function (dispatch: Dispatch<any>, state: () => RootState) {

        try {
            dispatch(setProcessing(true));

            // Option flagged as "Won" is created as a separate plan.
            // Each Plan will have a version of each applicable benefit (Medical + Ancillary, Dental).  Where there are multiples, create a version of the applicable benefits per Plan.
            const xState = state();

            const importModule = await import("global/Transform/QuoteRequestToGroupConfigurationTransformer")
            const { GroupConfigurationToQuoteRequestTransformer } = importModule;
            const results = await GroupConfigurationToQuoteRequestTransformer(xState, options);

            var snapshot = await dataSource.createGroupConfigurationSnapshot(results.snapshotData,);

            dispatch(
                updateSnapshotData({
                    json: results.json,
                    snapshot: snapshot,
                })
            );

            const instructions = {};
            // // latest redux state
            // const saveResult = await SaveAsync(dispatch, dataSource, state(), instructions);

            // // open the new one
            // (window as Window).open(AppLinkToExternal({
            //     id: saveResult.snapshot?.bcbs_groupconfigurationsnapshotid!,
            //     type: 'bcbs_groupconfigurationsnapshot'
            // }));

            dispatch(updateSnapshotBlobVersion(null));

        }
        catch (error: any) {
            alert("An error has occurred. This page cannot load.");

            // lets create a new one is payload is not providing one
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });

            throw error;

        } finally {
            dispatch(setProcessing(false));
        }

    }
}